import './styles.css';
import { EVENTS } from '@sustainbit/tracking';
import { initAnalytics } from './analytics.ts';

const analytics = initAnalytics();

// ── Bit Protagonist — birth, arrival, ignition, breath ─────────────────────
// The canvas Bit owns the hero eyebrow slot for the lifetime of the page.
// It moves through six states:
//
//   birth         (0–1200ms)   Mark inhales → births Bit → tether → snap.
//                              Snap overshoot is gentle (105% not 115%).
//   deceleration  (1200–1500)  Bit coasts into the eyebrow with pure ease-
//                              out + sub-pixel jitter + 1px compression on
//                              contact. No elastic ring.
//   recovery      (1500–2200)  Stillness. Bit dims to 50% brightness and
//                              desaturates toward grey-copper. Veil completes
//                              its final lift during THIS window so the world
//                              brightens while the protagonist is quietest.
//   ignition      (2200–2700)  First breath. Brightness 0.5 → 1.4 on a fast-
//                              rise / slow-fall envelope. Copper halo blooms
//                              outward with mix-blend-mode: screen so it
//                              actually lifts surrounding pixels. Hue warms
//                              toward gold at peak. Hero h1 ticks +4% bright.
//   settling      (2700–3300)  Halo dissolves, brightness eases to baseline
//                              1.0, hue returns to copper.
//   alive         (3300→∞)     Continuous breath. Asymmetric envelope (quick
//                              rise, slow fall), perlin-drifted period,
//                              brightness and scale coupled with a 100ms
//                              phase offset, hue drifts with brightness,
//                              irregular heartbeat pulses every 8–15s, long
//                              breath-holds every ~60s. Pauses on visibility
//                              loss / hero out of view; resumes cleanly.
//
// Why a single long-lived canvas instead of canvas-then-static-span:
// the alive loop's nuance (asymmetric envelope, perlin period drift,
// heartbeat, hue-coupled brightness) can't be expressed in CSS, and the
// hand-off from canvas to static span would visibly snap brightness.
//
// Why the halo is a separate div, not a canvas draw: mix-blend-mode: screen
// on a div literally brightens the page pixels behind it. A canvas radial
// gradient just paints a soft circle on top — reads as a notification
// badge, not as light.
//
// Skipped on prefers-reduced-motion or mid-scroll loads (refresh, deep
// link, restored session) — the cinematic only reads at the top.

interface BitAnchor { x: number; y: number; size: number }
interface BitState {
  bitX: number;
  bitY: number;
  bitW: number;
  bitH: number;
  /** Alpha of the bit's own fill — modulates SUBTLY (0.90–1.0 typical) so
   * the bit's color doesn't visibly cycle. The breath is felt in the glow,
   * not in the bit. */
  bodyAlpha: number;
  /** Canvas shadow blur in CSS-px — modulates STRONGLY (3–18 across phases).
   * This is where the visible breathing lives: the soft copper glow around
   * the bit expanding and contracting. */
  glowBlur: number;
  /** Alpha of the canvas shadow color — modulates with the glow blur. */
  glowAlpha: number;
}

// ── Phase boundaries (ms) ──────────────────────────────────────────────────
// Birth ends at T_SNAP_END (no separate "post-snap settle"); deceleration
// takes over there and coasts the bit from 1.05 travel back to 1.0 over
// 680ms. The prior version had a teleport at the birth→decel boundary
// (birth landed at 1.0, decel hardcoded restart at 1.05) which read as a
// visible "kick" right when the bit arrived. Removing it gave the bit a
// single continuous deceleration from snap overshoot to rest.
const T_PRESSURE_END = 200;
const T_BUD_END = 550;
const T_TETHER_END = 700;
const T_SNAP_END = 820;
const T_BIRTH_END = T_SNAP_END;
const T_DECEL_END = 1500;
const T_RECOVERY_END = 2200;
const T_IGNITION_END = 2700;
const T_SETTLING_END = 3300;

// When the hero glow fades in — keyed to the Bit coming to rest. The Bit
// stops translating at T_DECEL_END (the physical "settle"); the breath/glow
// finish igniting later at T_SETTLING_END. Tied to the physical settle so
// the wash reads as caused by the Bit landing. One-line tweak to retime.
const T_GLOW_REVEAL = T_DECEL_END;

// ── Copper ─────────────────────────────────────────────────────────────────
// Matches the wordmark's --copper: oklch(0.739, 0.167, 42.8) ≈ rgb(255,130,76).
// One copper everywhere (wordmark Bit, flying Bit, tether, glow). Modulating
// alpha + shadow gives the visible life; the hue itself never changes,
// which is what kept the previous hue-interpolating version reading as a
// "wash cycling" effect rather than as one steady object that breathes.
const COPPER_RGB = '255,130,76';

class BitProtagonist {
  // Sizes tuned for landing-static. Eyebrow square is 30px, so the canvas
  // Bit lands at 30px. Tether/flash are 1.5× the landing values (which
  // targeted a 10px eyebrow) so they read proportional to the larger child.
  private static readonly TARGET_SIZE = 15;
  private static readonly TETHER_WIDTH_MAX = 3.6;
  private static readonly TETHER_WIDTH_MIN = 1.2;
  private static readonly FLASH_RADIUS_MIN = 22;
  private static readonly FLASH_RADIUS_MAX = 36;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly veil: HTMLElement;
  private readonly source: SVGPathElement;
  private readonly target: HTMLElement;
  private parent: BitAnchor = { x: 0, y: 0, size: 5 };
  private targetA: BitAnchor = { x: 0, y: 0, size: BitProtagonist.TARGET_SIZE };
  // Bottom of the sticky header in viewport y. Used after settling to fade
  // the bit out as it scrolls up behind the header, so it stops painting on
  // top of the wordmark/CTA. Stable across scroll (header is sticky), but
  // remeasured for safety since the header height can change at the md:
  // breakpoint when the nav pill appears/disappears.
  private headerBottom = 60;
  // Cached once — the sticky header element never changes identity, so
  // re-querySelector-ing it on every scroll re-measure was pure waste (N22).
  private headerEl: HTMLElement | null = null;
  private headerQueried = false;

  private startMs = 0;
  private running = false;
  // Heartbeat schedule. nextHeartbeatAt is when the next pulse fires;
  // heartbeatActiveUntil is when the current pulse fully decays.
  private nextHeartbeatAt = 0;
  private heartbeatActiveUntil = 0;
  // Long-pause schedule. nextLongPauseAt fires; pauseUntil holds breath
  // at baseline until that time.
  private nextLongPauseAt = 0;
  private pauseUntil = 0;
  // Visibility-driven pause. When the tab is hidden or the hero scrolls
  // out of view, ease breath down to baseline over 400ms and stop drawing.
  // sleepFactor goes 1 (awake) ↔ 0 (asleep), interpolated by sleepStart/Until.
  private sleeping = false;
  private sleepFactor = 1;
  private sleepTargetAt = 0;
  private sleepStartAt = 0;
  private sleepStartFactor = 1;
  // Whether the canvas currently holds painted pixels. Lets an occluded or
  // asleep frame skip the per-frame clearRect (the page's only full-viewport
  // raster) yet still clear exactly once when the bit first leaves view, so
  // it doesn't linger behind the header.
  private canvasDirty = false;
  // Called by wake() to re-arm the rAF loop after it self-suspended on a
  // fully-asleep frame (sleepFactor reached 0). Set by the boot IIFE that
  // owns the loop. The breath itself is byte-identical to the always-on
  // version while visible; this only stops wasted frames when nothing is
  // being looked at.
  private requestWake: (() => void) | null = null;

  constructor(opts: {
    canvas: HTMLCanvasElement;
    veil: HTMLElement;
    source: SVGPathElement;
    target: HTMLElement;
  }) {
    this.canvas = opts.canvas;
    this.veil = opts.veil;
    this.source = opts.source;
    this.target = opts.target;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('boot-canvas: no 2d context');
    this.ctx = ctx;
    this.measureSurface();
    this.measureAnchors();
  }

  measureSurface(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssW = window.innerWidth;
    const cssH = window.innerHeight;
    this.canvas.width = Math.round(cssW * dpr);
    this.canvas.height = Math.round(cssH * dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  measureAnchors(): void {
    const sRect = this.source.getBoundingClientRect();
    const tRect = this.target.getBoundingClientRect();
    this.parent = {
      x: sRect.left + sRect.width / 2,
      y: sRect.top + sRect.height / 2,
      size: Math.max(3, Math.min(sRect.width, sRect.height)),
    };
    this.targetA = {
      x: tRect.left + tRect.width / 2,
      y: tRect.top + tRect.height / 2,
      size: BitProtagonist.TARGET_SIZE,
    };
    if (!this.headerQueried) {
      this.headerEl = document.querySelector<HTMLElement>('header[data-header]');
      this.headerQueried = true;
    }
    if (this.headerEl) this.headerBottom = this.headerEl.getBoundingClientRect().bottom;
  }

  start(nowMs: number): void {
    this.startMs = nowMs;
    this.running = true;
    // Schedule first heartbeat 8–15s out, first long-pause 50–70s out.
    this.nextHeartbeatAt = nowMs + T_SETTLING_END + 8000 + Math.random() * 7000;
    this.nextLongPauseAt = nowMs + T_SETTLING_END + 50000 + Math.random() * 20000;
  }

  /** Register the loop's re-arm hook. The boot IIFE owns the rAF; when a
   * draw() lands fully asleep (sleepFactor === 0) the loop stops scheduling,
   * and wake() calls this to resume it. */
  setWakeHandler(fn: () => void): void {
    this.requestWake = fn;
  }

  sleep(): void {
    if (this.sleeping) return;
    this.sleeping = true;
    this.sleepStartAt = performance.now();
    this.sleepStartFactor = this.sleepFactor;
    this.sleepTargetAt = this.sleepStartAt + 400;
  }

  wake(): void {
    if (!this.sleeping) return;
    this.sleeping = false;
    this.sleepStartAt = performance.now();
    this.sleepStartFactor = this.sleepFactor;
    this.sleepTargetAt = this.sleepStartAt + 400;
    // The loop may have self-suspended on a fully-asleep frame; re-arm it so
    // the 400ms ease-back-up and the resumed breath actually paint.
    this.requestWake?.();
  }

  private updateSleepFactor(nowMs: number): void {
    const target = this.sleeping ? 0 : 1;
    if (this.sleepFactor === target) return;
    const span = Math.max(1, this.sleepTargetAt - this.sleepStartAt);
    const u = Math.max(0, Math.min(1, (nowMs - this.sleepStartAt) / span));
    this.sleepFactor = lerp(this.sleepStartFactor, target, smoothstep01(u));
  }

  private clearCanvas(): void {
    if (!this.canvasDirty) return;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvasDirty = false;
  }

  /** Render one frame. Returns 'idle' when the bit has fully settled asleep
   * (sleepFactor === 0) and the next frame would be byte-identical — the
   * boot loop uses that to stop scheduling rAF until wake(). Returns 'live'
   * whenever anything is still changing so the loop keeps running. The
   * visible motion is unchanged: an idle frame is the same calm-baseline
   * frame the always-on loop would have repainted forever. */
  draw(nowMs: number): 'live' | 'idle' {
    if (!this.running) return 'idle';
    this.updateSleepFactor(nowMs);
    const t = nowMs - this.startMs;

    // BitState carries body + glow as separate channels (decoupled from a
    // single "brightness" multiplier). bodyAlpha is the bit-itself alpha
    // (modulates SUBTLY — the bit's color shouldn't visibly cycle). glowBlur
    // and glowAlpha are the canvas shadow channel — modulates STRONGLY
    // because the visible breathing lives in the glow, not in the bit.
    let s: BitState = {
      bitX: this.parent.x,
      bitY: this.parent.y,
      bitW: 0,
      bitH: 0,
      bodyAlpha: 0,
      glowBlur: 4,
      glowAlpha: 0.30,
    };
    let tetherAlpha = 0;
    let tetherWidth = BitProtagonist.TETHER_WIDTH_MIN;
    let snapFlash = 0;

    if (t < T_BIRTH_END) {
      const r = this.computeBirth(t);
      s = r.s;
      tetherAlpha = r.tetherAlpha;
      tetherWidth = r.tetherWidth;
      snapFlash = r.snapFlash;
    } else if (t < T_DECEL_END) {
      s = this.computeDeceleration(t);
    } else if (t < T_RECOVERY_END) {
      s = this.computeRecovery();
    } else if (t < T_IGNITION_END) {
      s = this.computeIgnition(t);
    } else if (t < T_SETTLING_END) {
      s = this.computeSettling();
    } else {
      s = this.computeAlive(nowMs, t);
    }

    // Sleep eases body+glow toward calm baseline (not toward zero — the bit
    // stays present while paused, just stops modulating).
    if (this.sleepFactor < 1) {
      s = {
        ...s,
        bodyAlpha: lerp(1.0, s.bodyAlpha, this.sleepFactor),
        glowBlur: lerp(4, s.glowBlur, this.sleepFactor),
        glowAlpha: lerp(0.30, s.glowAlpha, this.sleepFactor),
      };
    }

    // Header occlusion fade — once the protagonist has settled, fade as it
    // scrolls up behind the sticky header so the canvas (z-101) doesn't
    // paint over the wordmark/nav/CTA. 20px fade band ending at headerBottom:
    // fully visible at +20, fully gone at headerBottom. Only applies after
    // settling so the birth journey (which travels through the header strip)
    // stays untouched.
    if (t > T_SETTLING_END) {
      const fade = smoothstep01((s.bitY - this.headerBottom) / 20);
      if (fade <= 0) {
        // Fully behind the header: clear once to erase the bit, then cost
        // zero raster on every subsequent occluded frame. The clearRect now
        // lives AFTER this early-return so an occluded bit never re-rasters
        // a full-viewport canvas (N27).
        this.clearCanvas();
        // Suspend only when also fully asleep — by then the hero IO has
        // fired sleep() (scrolling the bit behind the header also scrolls
        // the hero out of view), so wake() will re-arm the loop on return.
        // While still awake (a fast scroll slipping the bit behind the
        // header before the IO fires) stay 'live' so nothing strands it.
        return this.sleeping && this.sleepFactor === 0 ? 'idle' : 'live';
      }
      s = {
        ...s,
        bodyAlpha: s.bodyAlpha * fade,
        glowAlpha: s.glowAlpha * fade,
      };
    }

    // We are going to paint: clear last frame's pixels first.
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvasDirty = true;

    this.updateVeil(t, s.bitX, s.bitY);

    if (tetherAlpha > 0.01) {
      this.drawTether(s.bitX, s.bitY, tetherAlpha, tetherWidth);
    }
    if (snapFlash > 0.01) {
      this.drawSnapFlash(snapFlash);
    }
    if (s.bitW > 0.5 && s.bitH > 0.5) {
      this.drawBit(s);
    }

    // Fully asleep (tab hidden or hero out of view, ease-down complete): the
    // bit is painted at its calm baseline and the next frame would be
    // byte-identical, so report idle and let the loop stop scheduling rAF
    // until wake(). While the 400ms ease is still in flight (sleepFactor
    // between 0 and 1) we stay 'live' so the ramp-down actually animates.
    if (this.sleeping && this.sleepFactor === 0) return 'idle';
    return 'live';
  }

  // ── Birth (0 → T_SNAP_END = 820ms) ──────────────────────────────────────
  // Four sub-phases: pressure, bud, tether, snap. Snap ends with the bit
  // at travel=1.05 and scale=1.05 — slight overshoot that deceleration
  // continuously eases back to 1.0. No separate "post-snap settle" stage;
  // its prior existence was what produced the visible kick at the boundary
  // (birth ended at travel=1.0, decel restarted at 1.05).
  private computeBirth(t: number): {
    s: BitState; tetherAlpha: number; tetherWidth: number; snapFlash: number;
  } {
    const pX = this.parent.x;
    const pY = this.parent.y;
    const target = this.targetA;
    let bitX = pX, bitY = pY, bitW = 0, bitH = 0;
    let aspectX = 1, aspectY = 1;
    let bodyAlpha = 0;
    let glowBlur = 3;
    let glowAlpha = 0;
    let tetherAlpha = 0;
    let tetherWidth = BitProtagonist.TETHER_WIDTH_MIN;
    let snapFlash = 0;

    if (t < T_PRESSURE_END) {
      // Mark does the work; canvas stays empty.
    } else if (t < T_BUD_END) {
      const u = (t - T_PRESSURE_END) / (T_BUD_END - T_PRESSURE_END);
      const e = smoothstep01(u);
      bodyAlpha = Math.min(1, e * 1.2);
      glowBlur = 3 + 2 * e;
      glowAlpha = 0.25 * e;
      const travel = e * 0.12;
      bitX = lerp(pX, target.x, travel);
      bitY = lerp(pY, target.y, travel);
      const size = target.size * 0.85 * e;
      bitW = size; bitH = size;
      tetherAlpha = 0.85 * e;
      tetherWidth = BitProtagonist.TETHER_WIDTH_MAX - (BitProtagonist.TETHER_WIDTH_MAX - BitProtagonist.TETHER_WIDTH_MIN) * e;
    } else if (t < T_TETHER_END) {
      const u = (t - T_BUD_END) / (T_TETHER_END - T_BUD_END);
      const e = smoothstep01(u);
      bodyAlpha = 1;
      glowBlur = 5;
      glowAlpha = 0.30;
      const scale = 0.85 + 0.15 * e;
      aspectX = 0.92 + 0.06 * e;
      aspectY = 1.10 - 0.06 * e;
      const travel = 0.12 + e * 0.46;
      bitX = lerp(pX, target.x, travel);
      bitY = lerp(pY, target.y, travel);
      bitW = target.size * scale * aspectX;
      bitH = target.size * scale * aspectY;
      tetherAlpha = 0.85 * (1 - e * 0.5);
      tetherWidth = BitProtagonist.TETHER_WIDTH_MIN + (1 - e) * 0.6;
    } else {
      // Snap (T_TETHER_END → T_SNAP_END = T_BIRTH_END).
      // Ends at travel=1.05, scale=1.05; deceleration takes over from
      // exactly there, so the transition is continuous (no kick).
      const u = (t - T_TETHER_END) / (T_SNAP_END - T_TETHER_END);
      const e = u * u;
      bodyAlpha = 1;
      glowBlur = 5 + 3 * e;
      glowAlpha = 0.30 + 0.10 * e;
      const scale = 1.0 + 0.05 * e;
      aspectX = 1.04;
      aspectY = 0.98;
      const travel = 0.58 + e * 0.47;
      bitX = lerp(pX, target.x, travel);
      bitY = lerp(pY, target.y, travel);
      bitW = target.size * scale * aspectX;
      bitH = target.size * scale * aspectY;
      tetherAlpha = 0.4 * (1 - u);
      tetherWidth = BitProtagonist.TETHER_WIDTH_MIN * 0.7;
      snapFlash = (1 - u) * (1 - u);
    }
    return {
      s: { bitX, bitY, bitW, bitH, bodyAlpha, glowBlur, glowAlpha },
      tetherAlpha, tetherWidth, snapFlash,
    };
  }

  // ── Deceleration (T_SNAP_END → T_DECEL_END = 820 → 1500ms) ──────────────
  // 680ms of pure cubic ease-out. Bit coasts from (travel=1.05, scale=1.05,
  // bodyAlpha=1.0, glow=8) at the start to (travel=1.0, scale=1.0,
  // bodyAlpha=0.85, glow=4) at the end. No compression pulse, no jitter,
  // no oscillation. The deceleration IS the arrival — the bit slows into
  // place, dims slightly as it "exhales from the journey," and the glow
  // contracts to its calm resting state.
  private computeDeceleration(t: number): BitState {
    const u = (t - T_BIRTH_END) / (T_DECEL_END - T_BIRTH_END);
    const e = 1 - Math.pow(1 - u, 3);
    const target = this.targetA;
    const fromX = lerp(this.parent.x, target.x, 1.05);
    const fromY = lerp(this.parent.y, target.y, 1.05);
    const bitX = lerp(fromX, target.x, e);
    const bitY = lerp(fromY, target.y, e);
    const scale = lerp(1.05, 1.0, e);
    const bitW = target.size * scale;
    const bitH = target.size * scale;
    return {
      bitX, bitY, bitW, bitH,
      bodyAlpha: lerp(1.0, 0.85, e),
      glowBlur:  lerp(8,   4,    e),
      glowAlpha: lerp(0.40, 0.28, e),
    };
  }

  // ── Recovery (1500–2200ms) ──────────────────────────────────────────────
  // Stillness. Bit holds at 85% body alpha, glow contracted to 3.5px /
  // 0.22 alpha — calm, tentative, "catching its breath." Veil completes
  // its final lift during this window (handled in updateVeil) so the
  // world brightens around the quiet protagonist.
  private computeRecovery(): BitState {
    return {
      bitX: this.targetA.x,
      bitY: this.targetA.y,
      bitW: this.targetA.size,
      bitH: this.targetA.size,
      bodyAlpha: 0.85,
      glowBlur: 3.5,
      glowAlpha: 0.22,
    };
  }

  // ── Ignition (2200–2700ms) ──────────────────────────────────────────────
  // First breath. The "fully lighting up" lives in the GLOW: blur expands
  // 3.5 → 16px (visible bloom outward from the bit), alpha intensifies
  // 0.22 → 0.60 (warm halo of light spilling onto the page around the
  // bit). The bit's body alpha rises from 0.85 → 1.0 — subtle, because
  // making the bit ITSELF flash brighter reads as a notification ping.
  // The bloom is what reads as ignition; the bit just becomes fully
  // present at the same time.
  private computeIgnition(t: number): BitState {
    const u = (t - T_RECOVERY_END) / (T_IGNITION_END - T_RECOVERY_END);
    const peakAt = 0.45;
    let env: number;
    if (u <= peakAt) {
      const v = u / peakAt;
      env = Math.sin(v * Math.PI / 2);     // fast rise
    } else {
      const v = (u - peakAt) / (1 - peakAt);
      env = Math.cos(v * Math.PI / 2);     // slow fall
    }
    // env: 0 → 1 → 0 across the ignition window.
    return {
      bitX: this.targetA.x,
      bitY: this.targetA.y,
      bitW: this.targetA.size,
      bitH: this.targetA.size,
      bodyAlpha: lerp(0.85, 1.0, Math.min(1, env * 2)),
      glowBlur:  lerp(3.5, 16,  env),
      glowAlpha: lerp(0.22, 0.60, env),
    };
  }

  // ── Settling (2700–3300ms) ──────────────────────────────────────────────
  // Glow contracts from the ignition tail back to alive's resting glow
  // (5px / 0.32 alpha). Body alpha stays at 1.0. A short calm beat
  // before continuous breathing starts.
  private computeSettling(): BitState {
    return {
      bitX: this.targetA.x,
      bitY: this.targetA.y,
      bitW: this.targetA.size,
      bitH: this.targetA.size,
      bodyAlpha: 1.0,
      glowBlur: 5,
      glowAlpha: 0.32,
    };
  }

  // ── Alive (3300ms → ∞) ──────────────────────────────────────────────────
  // Continuous breathing. The breath is FELT in the glow, not in the bit:
  //
  //   body alpha:   0.93 ↔ 1.0     subtle alpha modulation, barely visible
  //   glow blur:    4   ↔ 8 px     visible expansion/contraction
  //   glow alpha:   0.28 ↔ 0.42    visible intensity modulation
  //   scale:        no change       — bit's geometry is fixed
  //
  // The bit looks constant; what breathes is the warm copper halo around
  // it. ~4s base period with ±0.6s drift via slow sin/cos product on time
  // (so two cycles never look identical). Heartbeat: +20% glow alpha
  // briefly every 20–40s — felt as a slight intensification rather than a
  // size pulse. Long pause: every ~60s, the breath holds at baseline for
  // ~1.5s.
  private computeAlive(nowMs: number, t: number): BitState {
    const aliveT = (t - T_SETTLING_END) / 1000;

    let env: number;  // 0..1 — breath phase envelope
    if (nowMs < this.pauseUntil) {
      env = 0.3;  // long-pause hold at a gentle midpoint
    } else {
      const drift = 0.6 * Math.sin(aliveT * 0.314) * Math.cos(aliveT * 0.178);
      const period = 4.0 + drift;
      const phase = (aliveT % period) / period;
      env = asymmetricEnvelope(phase);
    }

    let glowBlur = lerp(4, 8, env);
    let glowAlpha = lerp(0.28, 0.42, env);
    let bodyAlpha = lerp(0.93, 1.0, env);

    // Heartbeat — felt as a brief glow intensification, not a body pulse.
    if (nowMs >= this.nextHeartbeatAt && nowMs >= this.heartbeatActiveUntil) {
      this.heartbeatActiveUntil = nowMs + 140;
      this.nextHeartbeatAt = nowMs + 140 + 20000 + Math.random() * 20000;
    }
    if (nowMs < this.heartbeatActiveUntil) {
      const hbU = (this.heartbeatActiveUntil - nowMs) / 140;
      const pulse = Math.sin(hbU * Math.PI);
      glowAlpha += 0.10 * pulse;
      glowBlur  += 1.5  * pulse;
    }

    // Long-pause scheduling.
    if (nowMs >= this.nextLongPauseAt && nowMs >= this.pauseUntil) {
      this.pauseUntil = nowMs + 1500;
      this.nextLongPauseAt = nowMs + 1500 + 50000 + Math.random() * 20000;
    }

    return {
      bitX: this.targetA.x,
      bitY: this.targetA.y,
      bitW: this.targetA.size,
      bitH: this.targetA.size,
      bodyAlpha,
      glowBlur,
      glowAlpha,
    };
  }

  private drawBit(s: BitState): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.shadowColor = `rgba(${COPPER_RGB},${s.glowAlpha.toFixed(3)})`;
    ctx.shadowBlur = s.glowBlur;
    ctx.fillStyle = `rgba(${COPPER_RGB},${s.bodyAlpha.toFixed(3)})`;
    ctx.fillRect(s.bitX - s.bitW / 2, s.bitY - s.bitH / 2, s.bitW, s.bitH);
    ctx.restore();
  }

  private drawTether(childX: number, childY: number, alpha: number, width: number): void {
    const ctx = this.ctx;
    const px = this.parent.x;
    const py = this.parent.y;
    const grad = ctx.createLinearGradient(px, py, childX, childY);
    grad.addColorStop(0, `rgba(${COPPER_RGB},${alpha})`);
    grad.addColorStop(1, `rgba(${COPPER_RGB},${alpha * 0.4})`);
    ctx.strokeStyle = grad;
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(px, py);
    const midX = (px + childX) / 2;
    const midY = (py + childY) / 2 + Math.min(12, Math.abs(childX - px) * 0.05);
    ctx.quadraticCurveTo(midX, midY, childX, childY);
    ctx.stroke();
  }

  private drawSnapFlash(intensity: number): void {
    const ctx = this.ctx;
    const px = this.parent.x;
    const py = this.parent.y;
    const r = BitProtagonist.FLASH_RADIUS_MIN + (BitProtagonist.FLASH_RADIUS_MAX - BitProtagonist.FLASH_RADIUS_MIN) * intensity;
    const grad = ctx.createRadialGradient(px, py, 0, px, py, r);
    grad.addColorStop(0, `rgba(${COPPER_RGB},${intensity * 0.55})`);
    grad.addColorStop(1, `rgba(${COPPER_RGB},0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(px - r, py - r, r * 2, r * 2);
  }

  /** Veil opacity:
   * - During birth (0–1200ms): keyed to Bit's measured travel distance.
   * - During deceleration (1200–1500): finishes from wherever travel left it.
   * - During recovery (1500–2200): smoothly eases to 0 over the recovery
   *   silence — the world keeps brightening while the protagonist is still.
   * - After 2200ms: stays at 0. */
  private updateVeil(t: number, bitX: number, bitY: number): void {
    let veilOp: number;
    if (t < T_BIRTH_END) {
      const totalDist = Math.hypot(this.targetA.x - this.parent.x, this.targetA.y - this.parent.y);
      const travelDist = Math.hypot(bitX - this.parent.x, bitY - this.parent.y);
      const travel = totalDist > 0 ? Math.min(1, travelDist / totalDist) : 0;
      veilOp = 0.7 * (1 - travel);
    } else if (t < T_RECOVERY_END) {
      // Smoothly drop the remaining veil opacity to 0 across the
      // deceleration + recovery window. Lifted-but-quiet world.
      const u = (t - T_BIRTH_END) / (T_RECOVERY_END - T_BIRTH_END);
      const e = smoothstep01(u);
      veilOp = 0.05 * (1 - e);
    } else {
      veilOp = 0;
    }
    this.veil.style.setProperty('--boot-veil-opacity', veilOp.toFixed(3));
  }
}

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function smoothstep01(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
}
/** Asymmetric breath envelope, 0..1 in, 0..1 out. Fast rise (sharp easing
 * 0→0.30 of cycle), hold (0.30→0.40), slow fall (0.40→0.85), pause at
 * bottom (0.85→1.0). */
function asymmetricEnvelope(phase: number): number {
  const p = phase % 1;
  if (p < 0.30) {
    // Inhale — sin easing for a sharp rise.
    const u = p / 0.30;
    return Math.sin(u * Math.PI / 2);
  }
  if (p < 0.40) {
    // Hold at top.
    return 1;
  }
  if (p < 0.85) {
    // Exhale — cos for a gentle fall.
    const u = (p - 0.40) / 0.45;
    return Math.cos(u * Math.PI / 2);
  }
  // Pause at bottom.
  return 0;
}

(function initBitProtagonist(): void {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const startsAtTop = window.scrollY < 50;
  const veil = document.getElementById('exposure-veil');
  const canvas = document.getElementById('boot-canvas') as HTMLCanvasElement | null;
  const source = document.querySelector<SVGPathElement>('[data-bit-source]');
  const target = document.querySelector<HTMLElement>('[data-bit-eyebrow]');

  if (!veil || !canvas || !source || !target) return;

  // Reduced-motion / mid-scroll skip: don't run the cinematic. The static
  // span behind the canvas is the source-of-truth eyebrow square in this
  // mode; we never mark .bit-alive, so it stays visible.
  if (reducedMotion || !startsAtTop) {
    veil.style.setProperty('--boot-veil-opacity', '0');
    // No cinematic to wait on — the Bit is already at rest, so reveal the
    // hero glow straight away (transition is disabled under reduced motion).
    document.body.classList.add('bit-settled');
    return;
  }

  // Hold the veil up at first paint — Bit drives it down as it travels.
  veil.style.setProperty('--boot-veil-opacity', '0.7');
  // Mark .bit-alive synchronously so the static eyebrow span is hidden
  // before the canvas paints its first frame (otherwise both would briefly
  // show together). Also drives the Mark CSS keyframe.
  document.body.classList.add('bit-alive');

  const protagonist = new BitProtagonist({ canvas, veil, source, target });

  const remeasure = (): void => {
    protagonist.measureSurface();
    protagonist.measureAnchors();
  };
  window.addEventListener('resize', remeasure);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(remeasure).catch(() => {});
  }
  // Track the eyebrow as it scrolls. Canvas is position:fixed (viewport
  // surface), so the bit was painting at the eyebrow's *original* viewport
  // y forever — visually it sat in the middle of the screen on top of
  // everything as the page scrolled past. Re-measuring on scroll lets the
  // bit move off-screen with the eyebrow and back in on scroll-up. Source
  // anchor (Mark, sticky header) stays viewport-stable, so re-reading it
  // is a no-op but cheap.
  //
  // rAF-coalesced (matching onScrollActive below): one re-measure per frame
  // at most, so a burst of scroll events forces layout once per frame rather
  // than once per event. Same visual result; the bit tracks the eyebrow at
  // frame cadence either way (N22).
  let anchorRaf = 0;
  window.addEventListener('scroll', () => {
    if (anchorRaf) return;
    anchorRaf = requestAnimationFrame(() => {
      anchorRaf = 0;
      protagonist.measureAnchors();
    });
  }, { passive: true });

  // Pause on visibility loss — saves battery and avoids the "frozen
  // mid-pulse when I came back" feeling. The sleep transition eases breath
  // to baseline over 400ms before stopping rAF.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) protagonist.sleep();
    else protagonist.wake();
  });

  // Pause when the hero scrolls out of view. We use the hero section as the
  // proxy for "is Bit being looked at?" — when it's offscreen, no one's
  // watching the breath; pause and save cycles.
  const heroSection = document.querySelector<HTMLElement>('section[data-mdx-id="hero"]');
  if (heroSection && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) protagonist.wake();
          else protagonist.sleep();
        }
      },
      { threshold: 0 },
    );
    io.observe(heroSection);
  }

  requestAnimationFrame((firstNow) => {
    protagonist.measureAnchors();
    protagonist.start(firstNow);
    let glowRevealed = false;
    // Loop self-suspends when draw() reports 'idle' (bit fully asleep, next
    // frame byte-identical) and re-arms from wake(). looping guards against
    // scheduling two concurrent rAF chains if wake() fires while still live.
    let looping = false;
    const tick = (now: number): void => {
      const status = protagonist.draw(now);
      // Reveal the hero glow once the Bit has come to rest. Driven off the
      // same rAF clock as the cinematic so it stays in lockstep even if the
      // tab was briefly throttled at boot.
      if (!glowRevealed && now - firstNow >= T_GLOW_REVEAL) {
        glowRevealed = true;
        document.body.classList.add('bit-settled');
      }
      // Hold the loop open until the glow reveal has fired even if the bit
      // somehow parks early, so the time-based reveal can never be stranded.
      if (status === 'idle' && glowRevealed) {
        looping = false;
        return;
      }
      requestAnimationFrame(tick);
    };
    const arm = (): void => {
      if (looping) return;
      looping = true;
      requestAnimationFrame(tick);
    };
    protagonist.setWakeHandler(arm);
    arm();
  });
})();

// Header layout is now pure CSS — flex justify-between with three slots
// (logo, nav pill, CTA). Mobile hides the nav pill via `hidden md:flex`,
// so justify-between collapses to logo-left / CTA-right naturally.

// Header CTA quiet-then-loud. A fully-lit "Start a project" button before
// the cold visitor has read what we do invites clicks they're not ready to
// make. CSS holds the button at opacity 0.4 until the visitor scrolls past
// the hero. Once loud it stays loud — scrolling back up doesn't re-dim it;
// the cold-visitor concern only applies to the first paint.
//
// Plain scroll listener, no rAF throttle. The listener is one-shot (it
// removes itself the moment the hero clears the viewport), so the usual
// scroll-perf concerns about per-event layout reads only apply for the
// few hundred ms before the visitor scrolls past the hero.
const ctaPrimary = document.querySelector<HTMLAnchorElement>('[data-cta-primary]');
const heroSection = document.querySelector<HTMLElement>('section[data-mdx-id="hero"]');
const scrollRail = document.querySelector<HTMLElement>('[data-scroll-rail]');
// Phone-only section index ("00 01 02 03 04"). Mirrors the desktop rail's
// reveal grammar: quiet until the cold visitor scrolls past the hero, then
// loud. Desktop keeps the side rail; this is the phone affordance only (the
// styles layer hides it at >=768px). Toggled in lockstep with the rail so the
// two surfaces never disagree about whether we are past the hero.
const mobileIndex = document.querySelector<HTMLElement>('[data-mobile-index]');
if ((ctaPrimary || scrollRail || mobileIndex) && heroSection) {
  const onCtaScroll = (): void => {
    if (heroSection.getBoundingClientRect().bottom <= 0) {
      if (ctaPrimary) ctaPrimary.dataset.state = 'loud';
      if (scrollRail) scrollRail.dataset.state = 'loud';
      if (mobileIndex) mobileIndex.dataset.state = 'loud';
      window.removeEventListener('scroll', onCtaScroll);
    }
  };
  window.addEventListener('scroll', onCtaScroll, { passive: true });
}

// ── Section active-state ──────────────────────────────────────────────────
// One scroll listener drives two things:
//   - the scroll-rail link whose section currently owns the top third of
//     the viewport lights up in copper
//   - the nav-pill link whose section group currently owns it lights up too
//
// Group mapping: the four content sections under the "How we work" block
// (how-we-work, what-we-see, what-we-build, how-we-transfer) all activate
// the "How we work" pill — the nav grammar collapses the four movements
// under their chapter heading even though the rail still surfaces each
// number 02–04 individually.
//
// Pure scroll listener (rAF-coalesced) rather than IntersectionObserver
// because IO with a top/bottom rootMargin band can leave the bottom of
// the page un-highlighted (the last section never enters the band before
// the page ends) — manual top-pick handles that boundary cleanly.
const railLinks = document.querySelectorAll<HTMLAnchorElement>('[data-rail-link]');
const navLinks = document.querySelectorAll<HTMLAnchorElement>('[data-nav-link]');
// Phone-only section index links. They carry the same hrefs (#story, …) as
// the rail, so we light the active one by matching its href hash to the rail's
// active section id — keeping the phone index in step with the side rail
// without a second source of truth for "which section is current".
const mobileIndexLinks = document.querySelectorAll<HTMLAnchorElement>('[data-mobile-index] a');
if (railLinks.length > 0 || navLinks.length > 0) {
  const sections: { id: string; el: HTMLElement; railLink: HTMLAnchorElement | null }[] = [];
  // Build the section list from rail links (authoritative — every numbered
  // section has a rail entry). Each section may or may not have a matching
  // rail link (it always does here, but the type allows future divergence).
  // 'start' is included so that when the reader reaches the contact form the
  // active section becomes 'start', which has no rail link and no nav bucket,
  // so the rail clears and no nav pill stays lit. Without it the last numbered
  // section ('who-we-are') would keep "04" / "Who we are" lit at the form,
  // wrongly claiming the page is still on the founder section (N38).
  const targetIds = ['story', 'how-we-work', 'what-we-see', 'what-we-build', 'how-we-transfer', 'who-we-are', 'start'];
  for (const id of targetIds) {
    const el = document.getElementById(id);
    if (!el) continue;
    const railLink = document.querySelector<HTMLAnchorElement>(`[data-rail-link][data-target="${id}"]`);
    sections.push({ id, el, railLink });
  }
  // Map each section id to the nav-pill bucket it activates.
  const navBucketForSection: Record<string, string> = {
    'story': 'story',
    'how-we-work': 'how-we-work',
    'what-we-see': 'how-we-work',
    'what-we-build': 'how-we-work',
    'how-we-transfer': 'how-we-work',
    'who-we-are': 'who-we-are',
  };

  // Which rail link stays lit for a section that has no rail entry of its own.
  // The 'how-we-work' poster sits between numbered sections; without this the
  // rail would read fully dark mid-page, so we hold "00" (story) lit through it.
  // 'start' deliberately has no entry here so the rail clears at the contact
  // form rather than stranding "04" lit (N38).
  const railFallbackForSection: Record<string, string> = {
    'how-we-work': 'story',
  };

  // Sliding indicator behind the active nav link. Measured against the
  // active link's offsetLeft / offsetWidth so the indicator's transform
  // and width animate from its previous position to the new one over
  // ~450ms (CSS-driven). On first paint we suppress the transition so
  // the indicator snaps to its initial position rather than sliding in
  // from x=0.
  const navIndicator = document.querySelector<HTMLElement>('[data-nav-indicator]');
  let navIndicatorPrimed = false;
  const positionNavIndicator = (activeLink: HTMLAnchorElement | null): void => {
    if (!navIndicator) return;
    if (!activeLink) {
      navIndicator.dataset.visible = 'false';
      return;
    }
    const setPos = (): void => {
      navIndicator.style.transform = `translateX(${activeLink.offsetLeft}px)`;
      navIndicator.style.width = `${activeLink.offsetWidth}px`;
      navIndicator.dataset.visible = 'true';
    };
    if (!navIndicatorPrimed) {
      // First paint: snap with no transition, then re-enable.
      navIndicator.dataset.noTransition = 'true';
      setPos();
      // Force reflow so the no-transition style is applied before we
      // remove the flag, otherwise the browser may coalesce both
      // changes into a single transitioning paint.
      void navIndicator.offsetWidth;
      delete navIndicator.dataset.noTransition;
      navIndicatorPrimed = true;
    } else {
      setPos();
    }
  };

  const updateActive = (): void => {
    // The "current" section is the last one whose top has crossed the
    // 30% fold line. Walking the list in document order and overwriting
    // gives us the right answer at the bottom of the page too.
    const fold = window.innerHeight * 0.30;
    let activeId = sections[0]?.id ?? '';
    for (const s of sections) {
      if (s.el.getBoundingClientRect().top <= fold) activeId = s.id;
    }
    // The rail lights its own section's link, or, for a section with no rail
    // entry (the 'how-we-work' poster), the fallback link, so the rail never
    // reads fully dark mid-page. 'start' has no fallback, so the rail clears.
    const activeHasRailLink = sections.some((s) => s.id === activeId && s.railLink);
    const railActiveId = activeHasRailLink ? activeId : (railFallbackForSection[activeId] ?? '');
    for (const s of sections) {
      if (s.railLink) {
        const isActive = s.id === railActiveId;
        if ((s.railLink.dataset.active === 'true') !== isActive) {
          s.railLink.dataset.active = isActive ? 'true' : 'false';
        }
      }
    }
    // Mirror the rail's active section onto the phone index (same hrefs).
    for (const link of mobileIndexLinks) {
      const isActive = link.hash === `#${railActiveId}`;
      if ((link.dataset.active === 'true') !== isActive) {
        link.dataset.active = isActive ? 'true' : 'false';
      }
    }
    const navTarget = navBucketForSection[activeId];
    let activeNavLink: HTMLAnchorElement | null = null;
    for (const link of navLinks) {
      const isActive = link.dataset.target === navTarget;
      if ((link.dataset.active === 'true') !== isActive) {
        link.dataset.active = isActive ? 'true' : 'false';
      }
      if (isActive) activeNavLink = link;
    }
    positionNavIndicator(activeNavLink);
  };

  // Re-measure on resize — link positions can shift if the viewport
  // narrows. Throttled with rAF to avoid thrashing during a drag-resize.
  let resizeRaf = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => {
      const current = Array.from(navLinks).find(l => l.dataset.active === 'true') ?? null;
      if (current) {
        // Snap during resize — animating while the viewport is changing
        // shape produces a janky double-motion.
        navIndicatorPrimed = false;
        positionNavIndicator(current);
      }
    });
  });

  let activeRaf = 0;
  const onScrollActive = (): void => {
    if (activeRaf) return;
    activeRaf = requestAnimationFrame(() => {
      activeRaf = 0;
      updateActive();
    });
  };
  window.addEventListener('scroll', onScrollActive, { passive: true });
  window.addEventListener('resize', onScrollActive, { passive: true });
  updateActive();
}

// Form submission: real POST to /api/contact with Turnstile token.
// On success, show inline status (extra text if double-opt-in confirmation
// was sent). On failure, surface the reason and let the user retry.
const form = document.querySelector<HTMLFormElement>('[data-contact-form]');
const sendBtn = document.querySelector<HTMLButtonElement>('[data-send]');
const statusEl = document.querySelector<HTMLDivElement>('[data-form-status]');
const emailInput = document.querySelector<HTMLInputElement>('#sap-email');
const messageInput = document.querySelector<HTMLTextAreaElement>('#sap-msg');
const subscribeInput = document.querySelector<HTMLInputElement>('#sap-subscribe');

// Lazy-load the Cloudflare Turnstile widget script. The widget itself pulls in
// ~340KB of third-party code + drops 6 cookies; loading it on initial pageload
// tanks both LCP and the Best Practices score for visitors who never touch the
// contact form. Inject it only when the form scrolls within view (or on the
// first focus into a form field), whichever comes first.
// Build-injected per-environment Turnstile sitekey (see vite.config.ts). Production ships the real key;
// staging / PR previews / dev ship Cloudflare's always-pass test key.
declare const __TURNSTILE_SITE_KEY__: string;

const turnstileContainer = document.querySelector<HTMLDivElement>('[data-turnstile]');
if (turnstileContainer) {
  // Set the sitekey BEFORE api.js renders the widget so the right key is used per environment. The MDX
  // keeps the production key as a static fallback, so a missing inject can never blank the widget in prod.
  if (typeof __TURNSTILE_SITE_KEY__ === 'string' && __TURNSTILE_SITE_KEY__) {
    turnstileContainer.dataset.sitekey = __TURNSTILE_SITE_KEY__;
  }
  let loaded = false;
  const load = (): void => {
    if (loaded) return;
    loaded = true;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  };
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            load();
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: '400px' },
    );
    io.observe(turnstileContainer);
  } else {
    // Old browsers without IO: load immediately so the form still functions.
    load();
  }
  // Belt-and-suspenders: if the user tabs into a form field before the
  // observer fires (e.g. via deeplink to #start with a long form below the
  // fold), kick off the load right away.
  form?.addEventListener('focusin', load, { once: true });
}

interface TurnstileGlobal {
  getResponse: (widgetId?: string) => string | undefined;
  reset: (widgetId?: string) => void;
}

function getTurnstile(): TurnstileGlobal | undefined {
  return (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
}

function setStatus(text: string, kind: 'idle' | 'success' | 'error' = 'idle') {
  if (!statusEl) return;
  statusEl.textContent = text;
  // The error colour is the designed --signal-error token (defined in
  // styles.css), not a pasted GitHub-dark red — it sits in the page's own
  // warm palette so the one error colour belongs with the copper/white system.
  statusEl.style.color =
    kind === 'success' ? 'var(--copper)' : kind === 'error' ? 'var(--signal-error)' : '';
}

// ── Client-side validation takeover (mirrors the server's contract) ──────────
// The server (form-submission-service.ts) is the source of truth and re-checks
// everything. These client checks ADD a fast, specific, token-free first pass
// so a too-short brief never spends a Turnstile token or round-trips to fail
// with a generic line. The floor (10 chars, on the trimmed message) mirrors
// MESSAGE_MIN; the cap mirrors MESSAGE_MAX. Keep these in step with the server.
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;

// Map the server's `details` codes (and our mirrored client-side codes) to
// calm, specific, fixable copy in the same plain voice as the field labels.
// The server returns these under `details` when error === 'invalid_input'
// (contact.ts) — email_invalid, message_too_short, message_too_long,
// jurisdiction_required, source_url_invalid.
type FieldErrorCode =
  | 'email_invalid'
  | 'email_required'
  | 'message_too_short'
  | 'message_too_long'
  | 'message_required'
  | 'jurisdiction_required'
  | 'source_url_invalid';

const FIELD_ERROR_COPY: Record<FieldErrorCode, string> = {
  email_required: 'We need an email to reply to.',
  email_invalid: 'That email looks off. Check it and try again.',
  message_required: 'Tell us a little about what you sense.',
  message_too_short: 'A line or two more helps us read it properly.',
  message_too_long: 'That is a lot to take in. Trim it to the essentials.',
  jurisdiction_required: 'We could not read your region. Try again in a moment.',
  source_url_invalid: 'Something went wrong on our side. Try again in a moment.',
};

// Which field a given code belongs beside. Codes with no field (jurisdiction,
// source url — values the visitor never types) fall back to the status line.
const FIELD_FOR_CODE: Partial<Record<FieldErrorCode, 'email' | 'message'>> = {
  email_required: 'email',
  email_invalid: 'email',
  message_required: 'message',
  message_too_short: 'message',
  message_too_long: 'message',
};

// Per-field error message elements, created lazily and parked inside each
// field's wrapper so the message sits beside the field it belongs to. The
// styles layer owns the look ([data-field-error]); we own create/show/clear
// and the invalid-state hook on the input itself.
const fieldErrorEls: { email: HTMLElement | null; message: HTMLElement | null } = {
  email: null,
  message: null,
};

function ensureFieldErrorEl(field: 'email' | 'message'): HTMLElement | null {
  if (fieldErrorEls[field]) return fieldErrorEls[field];
  const input = field === 'email' ? emailInput : messageInput;
  if (!input || !input.parentElement) return null;
  const el = document.createElement('p');
  el.dataset.fieldError = field;
  el.id = `sap-${field === 'email' ? 'email' : 'msg'}-error`;
  el.setAttribute('aria-live', 'polite');
  el.hidden = true;
  input.parentElement.appendChild(el);
  fieldErrorEls[field] = el;
  return el;
}

function setFieldError(field: 'email' | 'message', message: string): void {
  const input = field === 'email' ? emailInput : messageInput;
  const el = ensureFieldErrorEl(field);
  if (input) {
    input.setAttribute('aria-invalid', 'true');
    input.dataset.invalid = 'true';
    if (el) input.setAttribute('aria-describedby', el.id);
  }
  if (el) {
    el.textContent = message;
    el.hidden = false;
  }
}

function clearFieldError(field: 'email' | 'message'): void {
  const input = field === 'email' ? emailInput : messageInput;
  const el = fieldErrorEls[field];
  if (input) {
    input.removeAttribute('aria-invalid');
    delete input.dataset.invalid;
    input.removeAttribute('aria-describedby');
  }
  if (el) {
    el.textContent = '';
    el.hidden = true;
  }
}

function clearAllFieldErrors(): void {
  clearFieldError('email');
  clearFieldError('message');
}

// Route a code to its field error (if it has one) or the status line otherwise.
function applyErrorCode(code: FieldErrorCode): void {
  const copy = FIELD_ERROR_COPY[code];
  const field = FIELD_FOR_CODE[code];
  if (field) setFieldError(field, copy);
  else setStatus(copy, 'error');
}

// Local mirror of the server's validate(): returns the same codes so a
// too-short brief is caught here, before a token is spent. Email uses the same
// shape check the server uses; message uses the trimmed-length floor/cap.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateClientSide(email: string, message: string): FieldErrorCode[] {
  const codes: FieldErrorCode[] = [];
  if (!email) codes.push('email_required');
  else if (!EMAIL_RE.test(email)) codes.push('email_invalid');
  if (!message) codes.push('message_required');
  else if (message.length < MESSAGE_MIN) codes.push('message_too_short');
  else if (message.length > MESSAGE_MAX) codes.push('message_too_long');
  return codes;
}

// Form-funnel instrumentation: started, field_focused, abandoned.
let formStarted = false;
let formCompleted = false;
let lastFieldFocused: string | null = null;

form?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach(
  (field) => {
    field.addEventListener('focus', () => {
      const fieldId = field.id || field.tagName.toLowerCase();
      lastFieldFocused = fieldId;
      if (!formStarted) {
        formStarted = true;
        analytics?.track(EVENTS.CONTACT_FORM_STARTED, {});
      }
      analytics?.track(EVENTS.CONTACT_FORM_FIELD_FOCUSED, { field: fieldId });
    });
  },
);

// Lift a field's invalid state the moment the visitor starts fixing it, so the
// error never lingers on a value they have already corrected.
emailInput?.addEventListener('input', () => clearFieldError('email'));
messageInput?.addEventListener('input', () => clearFieldError('message'));

document.addEventListener('visibilitychange', () => {
  if (
    document.visibilityState === 'hidden' &&
    formStarted &&
    !formCompleted
  ) {
    analytics?.track(EVENTS.CONTACT_FORM_ABANDONED, {
      last_field: lastFieldFocused,
    });
    void analytics?.flush();
  }
});

// In-flight guard. We use a flag plus aria-disabled rather than the disabled
// ATTRIBUTE on the Send button: a disabled element is removed from the tab
// order, so an Enter-key submit that disables the focused button drops focus
// to <body>, stranding keyboard users mid-flow (N1). aria-disabled keeps the
// button focusable and the in-flight flag stops re-entry; the role="status"
// region still announces Sending/Sent.
let submitting = false;
// When the visitor presses Send before the Turnstile token is ready, we hold
// the submission and fire it automatically once the token arrives, rather than
// asking them to retry by hand (N18). This holds the pending intent.
let pendingSendUntil = 0;
let tokenWaitTimer = 0;

function setSendInFlight(inFlight: boolean, label: string): void {
  if (!sendBtn) return;
  submitting = inFlight;
  sendBtn.textContent = label;
  if (inFlight) sendBtn.setAttribute('aria-disabled', 'true');
  else sendBtn.removeAttribute('aria-disabled');
}

// The real POST. Returns once the outcome has been reflected to the UI. The
// caller owns the in-flight lifecycle so the deferred (token-arrived) path and
// the direct path share one network body.
async function sendBrief(email: string, message: string, token: string, originalLabel: string): Promise<void> {
  const ts = getTurnstile();
  setSendInFlight(true, 'Sending');
  setStatus('Sending');

  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        message,
        sourceUrl: window.location.href,
        subscribeToUpdates: subscribeInput?.checked === true,
        turnstileToken: token,
        visitorId: analytics?.visitorId,
      }),
    });
    const data = (await response.json().catch(() => ({}))) as {
      ok?: boolean;
      error?: string;
      details?: readonly string[];
      subscriptionPending?: boolean;
    };

    if (response.ok && data.ok) {
      formCompleted = true;
      analytics?.track(EVENTS.CONTACT_FORM_COMPLETED, {
        brief_length: message.length,
        subscription_pending: data.subscriptionPending === true,
      });
      void analytics?.flush();
      // The opt-in is recorded as pending server-side, but no confirmation
      // email is sent yet (no provider wired, see AR-1e). Do not claim an
      // inbox message until a real provider exists, or the page promises
      // something that never arrives. The opt-in capture itself is untouched.
      const baseMessage = 'Sent. We read every brief by hand.';
      setStatus(baseMessage, 'success');
      clearAllFieldErrors();
      if (form) form.reset();
      ts?.reset();
      setSendInFlight(true, 'Sent');
      window.setTimeout(() => {
        setSendInFlight(false, originalLabel);
      }, 4000);
    } else if (data.error === 'invalid_input' && Array.isArray(data.details) && data.details.length > 0) {
      // Map the server's specific reasons to field-level copy. A code with a
      // field lands beside that field; field-less codes (jurisdiction, source
      // url) fall back to the status line via applyErrorCode.
      const known = new Set<string>([
        'email_invalid', 'email_required', 'message_too_short', 'message_too_long',
        'message_required', 'jurisdiction_required', 'source_url_invalid',
      ]);
      let routedToField = false;
      for (const code of data.details) {
        if (known.has(code)) {
          if (FIELD_FOR_CODE[code as FieldErrorCode]) routedToField = true;
          applyErrorCode(code as FieldErrorCode);
        }
      }
      // If every returned code routed to a field, keep the status line quiet
      // rather than echoing a generic line on top of the specific field notes.
      if (!routedToField) setStatus('Please check your email and brief.', 'error');
      ts?.reset();
      setSendInFlight(false, originalLabel);
    } else {
      const reason =
        data.error === 'rate_limited'
          ? 'Too many submissions. Try again in a minute.'
          : data.error === 'turnstile_failed'
            // The widget already reset()s, so one more press of Send is all it
            // takes. Never say "refresh" — a refresh would discard the brief.
            ? 'Bot check did not pass. Press Send once more.'
            : data.error === 'invalid_input'
              ? 'Please check your email and brief.'
              : 'Something went wrong. Try again or email hello@sustainbit.com.';
      setStatus(reason, 'error');
      ts?.reset();
      setSendInFlight(false, originalLabel);
    }
  } catch {
    setStatus('Network error. Try again or email hello@sustainbit.com.', 'error');
    ts?.reset();
    setSendInFlight(false, originalLabel);
  }
}

form?.addEventListener('submit', (e) => {
  e.preventDefault();
  if (!form || !sendBtn || !emailInput || !messageInput) return;
  // In-flight guard: ignore re-entrant submits (Enter held, double-tap) while
  // a send is already running or a Sent state is settling.
  if (submitting) return;

  const email = emailInput.value.trim();
  const message = messageInput.value.trim();

  // Client validation takeover: catch field problems here, BEFORE touching
  // Turnstile, so a too-short brief shows a specific inline note and spends no
  // token. The server re-checks all of this; these are an additive first pass.
  clearAllFieldErrors();
  const codes = validateClientSide(email, message);
  if (codes.length > 0) {
    for (const code of codes) applyErrorCode(code);
    // Move focus to the first field with a problem so the fix is one keystroke
    // away and the inline note is announced.
    const firstField = codes.map((c) => FIELD_FOR_CODE[c]).find(Boolean);
    if (firstField === 'email') emailInput.focus();
    else if (firstField === 'message') messageInput.focus();
    return;
  }

  const originalLabel = sendBtn.textContent ?? 'Send';
  const ts = getTurnstile();
  const token = ts?.getResponse() ?? '';

  if (!token) {
    // Token not ready yet (widget still loading or solving). Hold the
    // submission and fire it automatically when the token arrives, rather than
    // making the visitor press Send again (N18). Show a calm holding line and
    // keep the button in its in-flight state so the page reads as working.
    setSendInFlight(true, 'Sending');
    setStatus('Verifying. One moment.');
    pendingSendUntil = Date.now() + 12000; // give the widget up to 12s
    if (tokenWaitTimer) window.clearInterval(tokenWaitTimer);
    tokenWaitTimer = window.setInterval(() => {
      const readyToken = getTurnstile()?.getResponse() ?? '';
      if (readyToken) {
        window.clearInterval(tokenWaitTimer);
        tokenWaitTimer = 0;
        void sendBrief(email, message, readyToken, originalLabel);
      } else if (Date.now() > pendingSendUntil) {
        // Widget never produced a token. Release the hold and let the visitor
        // retry with one more press — never tell them to refresh.
        window.clearInterval(tokenWaitTimer);
        tokenWaitTimer = 0;
        setStatus('Bot check did not load. Press Send once more.', 'error');
        setSendInFlight(false, originalLabel);
      }
    }, 400);
    return;
  }

  void sendBrief(email, message, token, originalLabel);
});

// ── Animated burst field ────────────────────────────────────────────────────
//
// Ported from packages/actors/src/{viewer-field,filament,emitter,attractor}.ts.
// Same algorithm the main landing app uses for the "eye in the field":
//
//   circle emitter (inner ring of start anchors)
//     ↓ filaments — each a many-segment polyline whose lateral deflection
//       breathes (sin) + jitters (perlin) with a per-filament phase offset
//   scattered ring attractor (outer ring of end anchors, wider radial jitter)
//
// Everything is computed each frame from `time`. Anchors are deterministic
// from `seed` so the silhouette is stable across reloads. Filament identity
// (phase) survives resize so the breath cycle never restarts visually.

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface Anchor { x: number; y: number }
interface Filament { phase: number }

interface FieldConfig {
  count: number;
  segments: number;
  emitterRadiusFrac: number;   // of min(w, h)
  attractorRadiusFrac: number; // of min(w, h)
  emitterJitter: number;
  attractorJitter: number;
  lateralAmpFrac: number;      // of min(w, h)
  wobbleRate: number;          // sin frequency of per-segment lateral sway
  wobbleAmp: number;           // scales the lateral sway envelope
  jitterAmp: number;
  strokeAlpha: number;
  tipAlpha: number;
  innerVoidFrac: number;       // of min(w, h)
  // Global "breath" — a slow, perceptible inhale/exhale of the whole field.
  // Modulates overall brightness AND the wobble rate of the strands (so the
  // motion feels coupled to the breath), but never the silhouette shape —
  // the pupil and halo radii stay constant.
  breathRate: number;          // rad/s of global pulse
  breathAlphaAmp: number;      // 0..1 — fractional brightness dilation at peak
  breathWobbleAmp: number;     // 0..1 — fractional wobble-rate modulation at peak
  seed: number;
}

// ── §02 cinematic — "the eye is born" ───────────────────────────────────────
// One-shot 3s intro that assembles the steady-state field from the pupil.
// Each endpoint is born by, in sequence:
//   1. Copper Bit blooms gently AT the existing endpoint position (ends[k]).
//   2. The existing white wobble strand grows from the pupil toward the
//      copper bit.
//   3. The copper Bit colour-flashes to white (the rgb interpolates
//      copper → white quickly; alpha is held).
//   4. Settled — white tip + full white strand, identical to the steady
//      render output at the same global time.
//
// No straight copper tether, no radial firework. The "tether" is the same
// white filament that lives in steady state, drawn progressively. By
// t=INTRO_DURATION the canvas converges byte-identically to renderSteady().
// See apps/landing-static/README.md for the symbolic frame.
const INTRO_DURATION = 5.0;          // s — total intro length
const INTRO_BIT_BLOOM = 0.30;        // s — copper bit fade-in at endpoint
const INTRO_STRAND_GROW = 0.80;      // s — white strand grows toward bit
const INTRO_STRAND_JITTER = 0.20;    // ±fraction on per-filament strand grow
const INTRO_HANDOFF = 0.35;          // s — copper→white colour flash
const INTRO_BIT_GLOW_BLUR = 3.5;     // px — soft copper glow on the bit

// Post-settle sporadic copper on the eye tips. Same heart-beat envelope
// as §03 (asymmetric: fast attack to copper, brief hold, slow decay back
// to white). N parallel tracks, each maintaining its own random fire
// interval between MIN and MAX — independent schedules across tracks.
const BURST_SPORADIC_PARALLEL = 10;   // independent tracks running in parallel
const BURST_SPORADIC_MIN = 2.0;      // s — minimum interval per track
const BURST_SPORADIC_MAX = 8.0;     // s — maximum interval per track
const BURST_SPORADIC_BLOOM = 0.30;   // s — copper rise on a pulse
const BURST_SPORADIC_HOLD = 0.80;    // s — copper sustain
const BURST_SPORADIC_FLASH = 0.50;   // s — copper→white return
const BURST_SPORADIC_TOTAL = BURST_SPORADIC_BLOOM + BURST_SPORADIC_HOLD + BURST_SPORADIC_FLASH;

const FIELD_CONFIG: FieldConfig = {
  count: 180,
  segments: 22,
  // Smaller emitter ring + matching smaller inner void → tendrils start
  // closer to center, void reads as a small pupil rather than a black hole.
  emitterRadiusFrac: 0.14,
  attractorRadiusFrac: 0.40,
  emitterJitter: 0.09,
  // Outer halo nearly uniform — only a hint of raggedness, matching Lovable.
  attractorJitter: 0.10,
  lateralAmpFrac: 0.04,
  // 20% of the prior wobble speed (0.28 → 0.056). Strands sway glacially;
  // the visible motion comes from per-frame perlin jitter and the global
  // breath, not the slow lateral sin sweep.
  wobbleRate: 0.0056,
  wobbleAmp: 0.45,
  jitterAmp: 0.10,
  strokeAlpha: 0.45,
  tipAlpha: 0.95,
  innerVoidFrac: 0.14,
  // ~11-second breath cycle. Brightness sways ±20% and the wobble rate
  // sways ±60% (so strands almost halt at exhale and pick back up at
  // inhale), but the silhouette shape never changes.
  breathRate: 1.35,
  breathAlphaAmp: 0.20,
  breathWobbleAmp: 1.60,
  seed: 0xb0a71fc,
};

function makeRng(seed: number): () => number {
  let s = (seed | 0) || 1;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    return (s >>> 0) / 4294967296;
  };
}

// Tiny Perlin-like 2D noise — same shape (smoothstep-blended grid hashes,
// signed -1..1 output) as packages/core/src/noise.ts, inlined to keep this
// file zero-dep. Used for per-segment lateral micro-jitter on each filament.
const PERM = (() => {
  const rng = makeRng(0xc0ffee);
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [p[i], p[j]] = [p[j], p[i]];
  }
  const ext = new Uint8Array(512);
  for (let i = 0; i < 512; i++) ext[i] = p[i & 255];
  return ext;
})();
function fade(t: number): number { return t * t * t * (t * (t * 6 - 15) + 10); }
function nlerp(a: number, b: number, t: number): number { return a + t * (b - a); }
function grad2d(hash: number, x: number, y: number): number {
  const h = hash & 3;
  return ((h & 1) ? -x : x) + ((h & 2) ? -y : y);
}
function noise2d(x: number, y: number): number {
  const xi = Math.floor(x) & 255;
  const yi = Math.floor(y) & 255;
  const xf = x - Math.floor(x);
  const yf = y - Math.floor(y);
  const u = fade(xf);
  const v = fade(yf);
  const aa = PERM[PERM[xi] + yi];
  const ab = PERM[PERM[xi] + yi + 1];
  const ba = PERM[PERM[xi + 1] + yi];
  const bb = PERM[PERM[xi + 1] + yi + 1];
  return nlerp(
    nlerp(grad2d(aa, xf, yf), grad2d(ba, xf - 1, yf), u),
    nlerp(grad2d(ab, xf, yf - 1), grad2d(bb, xf - 1, yf - 1), u),
    v,
  );
}

class BurstField {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private cfg: FieldConfig;
  private starts: Anchor[] = [];
  private ends: Anchor[] = [];
  private filaments: Filament[] = [];
  private cssW = 0;
  private cssH = 0;
  private cx = 0;
  private cy = 0;
  private innerR = 0;
  private lateralAmp = 0;
  private time = 0;
  private lastT = 0;
  private rafId = 0;
  private running = false;
  private inView = true;
  // ── Intro phase (§02 cinematic) ───────────────────────────────────────
  // armed: triggerIntro() has been called (first viewport intersection).
  // done: localT exceeded INTRO_DURATION → steady render takes over.
  // elapsed: only ticks while inView, so an interrupted intro freezes
  // mid-flight rather than running invisibly off-screen.
  private introArmed = false;
  private introDone = false;
  private introElapsed = 0;
  private birthAt: number[] = [];
  private drawDur: number[] = [];
  // Sporadic copper heart-beat on tips, post-settle. N parallel tracks,
  // each picking its own random interval between MIN and MAX. Scheduled
  // when introDone flips true. Pulses target random tip indices and run
  // through the BLOOM → HOLD → FLASH envelope independently.
  private nextSporadicAt: number[] = [];
  private activePulses: Pulse[] = [];
  private sporadicRng: () => number = makeRng(0xb14571c);

  constructor(canvas: HTMLCanvasElement, cfg: FieldConfig = FIELD_CONFIG) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
    this.cfg = cfg;
    this.buildFilaments();
    this.measure();
  }

  private measure(): void {
    const cssW = this.canvas.clientWidth;
    const cssH = this.canvas.clientHeight;
    if (cssW === 0 || cssH === 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(cssW * dpr);
    this.canvas.height = Math.round(cssH * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.cssW = cssW;
    this.cssH = cssH;
    const minDim = Math.min(cssW, cssH);
    this.cx = cssW / 2;
    this.cy = cssH / 2;
    this.innerR = minDim * this.cfg.innerVoidFrac;
    this.lateralAmp = minDim * this.cfg.lateralAmpFrac;
    this.placeAnchors(minDim);
  }

  private placeAnchors(minDim: number): void {
    const c = this.cfg;
    const eR = minDim * c.emitterRadiusFrac;
    const aR = minDim * c.attractorRadiusFrac;
    const rngE = makeRng(c.seed);
    const rngA = makeRng(c.seed ^ 0x9e3779b1);
    this.starts = [];
    this.ends = [];
    // Emitter: tight circle, low radial+angular jitter.
    for (let i = 0; i < c.count; i++) {
      const baseAngle = (i / c.count) * Math.PI * 2;
      const aJit = (rngE() - 0.5) * c.emitterJitter * 0.3;
      const rJit = (rngE() - 0.5) * c.emitterJitter * eR;
      const a = baseAngle + aJit;
      const r = eR + rJit;
      this.starts.push({ x: this.cx + Math.cos(a) * r, y: this.cy + Math.sin(a) * r });
    }
    // Attractor: scattered outer ring, mostly radial jitter (70/30 split,
    // matching scatteredRingAttractor in packages/actors).
    const radialShare = c.attractorJitter * 0.7;
    const angularShare = c.attractorJitter * 0.3;
    for (let i = 0; i < c.count; i++) {
      const baseAngle = (i / c.count) * Math.PI * 2;
      const aJit = (rngA() - 0.5) * angularShare * 2 * (Math.PI / c.count);
      const rJit = (rngA() - 0.5) * radialShare * aR * 2;
      const a = baseAngle + aJit;
      const r = aR + rJit;
      this.ends.push({ x: this.cx + Math.cos(a) * r, y: this.cy + Math.sin(a) * r });
    }
  }

  private buildFilaments(): void {
    const c = this.cfg;
    const rng = makeRng(c.seed ^ 0x517cc1b7);
    this.filaments = [];
    for (let i = 0; i < c.count; i++) {
      this.filaments.push({ phase: rng() * Math.PI * 2 });
    }
    this.computeIntroSchedule();
  }

  // Stratified birthAt — divide birth window into N slots, shuffle, place
  // each filament in one slot with mild within-slot jitter. Produces
  // organic spread (no clumps, no obvious sweep) deterministically.
  // drawDur is the per-filament strand-grow duration, jittered ±20% so
  // no two strands feel identical.
  private computeIntroSchedule(): void {
    const N = this.cfg.count;
    const rng = makeRng(this.cfg.seed ^ 0x5eedb1d1);
    const perTetherMax =
      INTRO_BIT_BLOOM +
      INTRO_STRAND_GROW * (1 + INTRO_STRAND_JITTER * 0.5) +
      INTRO_HANDOFF;
    const birthWindow = Math.max(0, INTRO_DURATION - perTetherMax);
    const slotW = N > 0 ? birthWindow / N : 0;
    const order: number[] = [];
    for (let i = 0; i < N; i++) order.push(i);
    for (let i = N - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    this.birthAt = new Array(N);
    this.drawDur = new Array(N);
    for (let k = 0; k < N; k++) {
      const slot = order[k];
      const jitter = (rng() - 0.5) * slotW * 0.6;
      this.birthAt[k] = Math.max(0, slot * slotW + jitter);
      this.drawDur[k] = INTRO_STRAND_GROW * (1 + INTRO_STRAND_JITTER * (rng() - 0.5));
    }
  }

  /** Arm the one-shot §02 intro. Called by IntersectionObserver in
   * initVisuals when the canvas first crosses ~40% intersection. Idempotent. */
  triggerIntro(): void {
    if (this.introArmed || this.introDone) return;
    this.introArmed = true;
    this.introElapsed = 0;
  }

  /** Skip the intro entirely — used under prefers-reduced-motion so the
   * canvas renders the steady state immediately rather than sitting at
   * the pre-armed pupil-only state. */
  skipIntro(): void {
    this.introArmed = true;
    this.introDone = true;
  }

  setInView(v: boolean): void {
    if (this.inView === v) return;
    this.inView = v;
    // Suspend the rAF entirely when off-view instead of running it every
    // frame with render() skipped (stop() was previously dead code). time/
    // introElapsed only advance while in-view, and start() re-seeds lastT,
    // so the cinematic freezes and resumes from the same frame — visually
    // identical, just no wasted wakeups while scrolled away (N28).
    if (v) this.start();
    else this.stop();
  }

  resize(): void {
    this.measure();
    this.render();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastT = performance.now() / 1000;
    const tick = (now: number): void => {
      if (!this.running) return;
      const t = now / 1000;
      const dt = Math.min(0.05, t - this.lastT);
      this.lastT = t;
      if (this.inView && !document.hidden) {
        this.time += dt;
        if (this.introArmed && !this.introDone) this.introElapsed += dt;
        this.render();
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  /** Render one frame. Branches: pre-armed (pupil only) → intro
   * (cinematic) → steady. Public so reduced-motion can call it once. */
  render(): void {
    if (this.cssW === 0 || this.cssH === 0) return;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.cssW, this.cssH);

    // Intro phase in flight.
    if (this.introArmed && !this.introDone) {
      if (this.introElapsed < INTRO_DURATION) {
        this.renderIntro(this.introElapsed);
        this.drawPupilMask();
        return;
      }
      this.introDone = true;
      // Schedule the first round of sporadic copper pulses on the eye
      // tips. Stagger initial fire times across tracks so they don't
      // synchronise — combine a fixed phase offset with a random jitter.
      const span = BURST_SPORADIC_MAX - BURST_SPORADIC_MIN;
      this.nextSporadicAt = [];
      for (let i = 0; i < BURST_SPORADIC_PARALLEL; i++) {
        const phase = (i / BURST_SPORADIC_PARALLEL) * BURST_SPORADIC_MIN;
        this.nextSporadicAt[i] = this.time + phase + this.sporadicRng() * span;
      }
    }

    // Pre-armed waiting state: empty pupil only. Visitor never sees this
    // outside the half-second before the IO trigger fires, since the IO
    // arms at ~40% intersection well before the canvas is centered.
    if (!this.introArmed && !this.introDone) {
      this.drawPupilMask();
      return;
    }

    // Steady state.
    this.renderSteady();
  }

  // ── Steady render ────────────────────────────────────────────────────
  // The breathing eye. White strands from pupil to outer ring, white tip
  // dots, black pupil mask. The intro converges byte-identically to this.

  private renderSteady(): void {
    const ctx = this.ctx;
    const c = this.cfg;
    const breath = Math.sin(this.time * c.breathRate);
    const alphaFactor = 1 + c.breathAlphaAmp * breath;
    const strokeA = clamp01(c.strokeAlpha * alphaFactor);
    const tipA = clamp01(c.tipAlpha * alphaFactor);
    const wobbleRateNow = Math.max(0, c.wobbleRate * (1 + c.breathWobbleAmp * breath));

    ctx.lineCap = 'round';
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = `rgba(255,255,255,${strokeA.toFixed(3)})`;
    this.drawStrands(this.time, wobbleRateNow);

    // Spawn the next sporadic pulse on any track whose nextSporadicAt
    // has elapsed. Each track is independent — picks its own random
    // target tip and its own next-fire delay.
    for (let trackIdx = 0; trackIdx < this.nextSporadicAt.length; trackIdx++) {
      if (this.time >= this.nextSporadicAt[trackIdx]) {
        const target = Math.floor(this.sporadicRng() * this.ends.length);
        this.activePulses.push({ cellIdx: target, startT: this.time });
        const span = BURST_SPORADIC_MAX - BURST_SPORADIC_MIN;
        this.nextSporadicAt[trackIdx] = this.time + BURST_SPORADIC_MIN + this.sporadicRng() * span;
      }
    }

    // Build copperToWhiteT override map for tips currently pulsing.
    // Cell starts at white (t=1), drifts toward copper (t→0) during
    // BLOOM, holds at copper (t=0) during HOLD, then eases back to
    // white during FLASH.
    const pulsing = new Map<number, number>();
    for (let i = this.activePulses.length - 1; i >= 0; i--) {
      const p = this.activePulses[i];
      const s = this.time - p.startT;
      if (s >= BURST_SPORADIC_TOTAL) {
        this.activePulses.splice(i, 1);
        continue;
      }
      let t: number;
      if (s < BURST_SPORADIC_BLOOM) {
        t = 1 - smoothstep01(s / BURST_SPORADIC_BLOOM);
      } else if (s < BURST_SPORADIC_BLOOM + BURST_SPORADIC_HOLD) {
        t = 0;
      } else {
        const u = (s - BURST_SPORADIC_BLOOM - BURST_SPORADIC_HOLD) / BURST_SPORADIC_FLASH;
        t = easeOutCubic(u);
      }
      // If two pulses land on the same tip, keep the one closer to
      // copper (smaller t). Unlikely in practice but defensive.
      const existing = pulsing.get(p.cellIdx);
      if (existing === undefined || t < existing) pulsing.set(p.cellIdx, t);
    }

    // Draw non-pulsing tips in a single fillStyle batch (the common case).
    ctx.fillStyle = `rgba(255,255,255,${tipA.toFixed(3)})`;
    for (let k = 0; k < this.ends.length; k++) {
      if (pulsing.has(k)) continue;
      const end = this.ends[k];
      ctx.beginPath();
      ctx.arc(end.x, end.y, 1.4, 0, Math.PI * 2);
      ctx.fill();
    }
    // Pulsing tips drawn separately with copper-to-white interpolation
    // and a fading copper glow (reuses the intro Bit primitive).
    for (const [k, t] of pulsing) {
      this.drawIntroBit(k, t, tipA);
    }

    this.drawPupilMask();
  }

  // ── Intro render ─────────────────────────────────────────────────────
  // Per-endpoint sequence (no straight tether, no firework):
  //   Phase 1 — copper Bit blooms gently at ends[k]
  //   Phase 2 — existing white wobble strand grows from pupil toward bit
  //             (bit stays visible at full alpha throughout)
  //   Phase 3 — copper Bit colour-flashes to white (rgb lerp; alpha held)
  //   Phase 4 — settled: white tip + full white strand
  //
  // At localT = INTRO_DURATION the canvas matches renderSteady() exactly.

  private renderIntro(localT: number): void {
    const ctx = this.ctx;
    const c = this.cfg;

    const breath = Math.sin(this.time * c.breathRate);
    const alphaFactor = 1 + c.breathAlphaAmp * breath;
    const targetStrokeA = clamp01(c.strokeAlpha * alphaFactor);
    const targetTipA = clamp01(c.tipAlpha * alphaFactor);
    const wobbleRateNow = Math.max(0, c.wobbleRate * (1 + c.breathWobbleAmp * breath));

    // Strands and bits use the same line-cap/width across the frame; set once.
    ctx.lineCap = 'round';
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = `rgba(255,255,255,${targetStrokeA.toFixed(3)})`;

    const N = this.cfg.count;
    for (let k = 0; k < N; k++) {
      const s = localT - this.birthAt[k];
      if (s < 0) continue;

      const grow = this.drawDur[k];
      const tBloomEnd = INTRO_BIT_BLOOM;
      const tGrowEnd = tBloomEnd + grow;
      const tHandEnd = tGrowEnd + INTRO_HANDOFF;

      if (s < tBloomEnd) {
        // Phase 1: copper bit blooms gently at endpoint.
        const bU = s / INTRO_BIT_BLOOM;
        const alpha = smoothstep01(bU) * targetTipA;
        this.drawIntroBit(k, 0, alpha);
      } else if (s < tGrowEnd) {
        // Phase 2: white strand grows from pupil toward bit; bit holds.
        const sU = (s - tBloomEnd) / grow;
        const progress = easeOutCubic(sU);
        this.drawSingleStrand(k, this.time, wobbleRateNow, progress);
        this.drawIntroBit(k, 0, targetTipA);
      } else if (s < tHandEnd) {
        // Phase 3: full strand visible; copper bit colour-flashes to white.
        this.drawSingleStrand(k, this.time, wobbleRateNow, 1);
        const hU = (s - tGrowEnd) / INTRO_HANDOFF;
        this.drawIntroBit(k, easeOutCubic(hU), targetTipA);
      } else {
        // Phase 4: settled. Same calls renderSteady would make.
        this.drawSingleStrand(k, this.time, wobbleRateNow, 1);
        this.drawWhiteTip(k, targetTipA);
      }
    }
  }

  // ── Shared draw primitives ───────────────────────────────────────────

  private drawStrands(time: number, wobbleRateNow: number): void {
    for (let k = 0; k < this.filaments.length; k++) {
      this.drawSingleStrand(k, time, wobbleRateNow, 1);
    }
  }

  /** Draw filament k as a wobbling white strand from its start to a
   * fraction `progress` along the chord toward its end. progress=1 is
   * the steady full strand. The wobble uses the same envelope, perlin
   * jitter, and breath-coupled rate as the steady render, so a strand
   * drawn at progress=1 during the intro is byte-identical to the
   * steady render at the same global time. */
  private drawSingleStrand(k: number, time: number, wobbleRateNow: number, progress: number): void {
    const ctx = this.ctx;
    const c = this.cfg;
    const segs = c.segments;
    const f = this.filaments[k];
    const start = this.starts[k];
    const end = this.ends[k];
    if (!f || !start || !end) return;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const len = Math.hypot(dx, dy);
    if (len < 0.001) return;
    const perpX = -dy / len;
    const perpY = dx / len;

    const p = clamp01(progress);
    const maxI = Math.max(1, Math.ceil(p * segs));

    ctx.beginPath();
    for (let i = 0; i <= maxI; i++) {
      // For partial draws, cap the last segment at the true progress
      // fraction so the strand grows continuously rather than in steps.
      const t = i < maxI ? i / segs : p;
      const baseX = start.x + dx * t;
      const baseY = start.y + dy * t;
      const envelope = Math.sin(t * Math.PI);
      const wobbleMod = 1 + c.wobbleAmp * 0.5 * Math.sin(time * wobbleRateNow * 0.3 + f.phase);
      const wobble = this.lateralAmp * Math.sin(time * wobbleRateNow + f.phase + t * 2.0) * wobbleMod;
      const jitter = noise2d(t * 4 + f.phase, time * 8) * c.jitterAmp * this.lateralAmp;
      const lat = envelope * (wobble + jitter);
      const x = baseX + perpX * lat;
      const y = baseY + perpY * lat;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  /** Draw a small bit at ends[k] whose rgb interpolates between copper
   * (copperToWhiteT=0) and white (copperToWhiteT=1). A soft copper glow
   * fades out across the transition so the white settled state has no
   * halo (matching the steady tip exactly). */
  private drawIntroBit(k: number, copperToWhiteT: number, alpha: number): void {
    const end = this.ends[k];
    if (!end) return;
    const ctx = this.ctx;
    const t = clamp01(copperToWhiteT);
    // Copper rgb (255,130,76) → white (255,255,255).
    const g = Math.round(130 + (255 - 130) * t);
    const b = Math.round(76 + (255 - 76) * t);
    const glow = INTRO_BIT_GLOW_BLUR * (1 - t);
    ctx.save();
    if (glow > 0.1) {
      ctx.shadowColor = `rgba(${COPPER_RGB},${(alpha * 0.6).toFixed(3)})`;
      ctx.shadowBlur = glow;
    }
    ctx.fillStyle = `rgba(255,${g},${b},${alpha.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(end.x, end.y, 1.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawWhiteTip(k: number, alpha: number): void {
    const end = this.ends[k];
    if (!end) return;
    const ctx = this.ctx;
    ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(end.x, end.y, 1.4, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawPupilMask(): void {
    // Inner void mask — crisp black circle hides the inner ring of starts
    // so the silhouette reads as filaments emerging FROM a void rather
    // than from a visible inner ring. Fixed size (no breath scaling).
    const ctx = this.ctx;
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.arc(this.cx, this.cy, this.innerR, 0, Math.PI * 2);
    ctx.fill();
  }
}

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v; }
function easeOutCubic(t: number): number {
  const u = clamp01(t);
  return 1 - (1 - u) * (1 - u) * (1 - u);
}

// ── §03 cinematic — "the mesh builds itself" ────────────────────────────────
//
// One-shot 10s construction of the §03 "What We Build" grid. Cells wake
// outward from centre by Chebyshev distance (with jitter so the wavefront
// reads organic, not geometric). Per cell:
//
//   1. Copper square blooms gently at the cell position (sharp, no halo).
//   2. Brief copper hold.
//   3. Copper rgb colour-flashes to white. Glow fades to zero.
//   4. Settled — white square remains. No fade-back, no restart.
//
// After every cell has settled (~t=10s), the grid stays fully white and a
// sporadic loop re-ignites one random cell at a time (interval 6–14s,
// asymmetric heart-beat envelope: fast attack, hold, slow decay back to
// white). Maintains the same copper-is-us / white-is-you grammar as §02.

interface BuildMeshConfig {
  cellSize: number;       // px between cell centers
  squareSize: number;     // px side length of each filled square (sharp, no halo)
  tipAlpha: number;       // settled white alpha (matches §02 tip alpha for cross-section consistency)
  // Slow global breath — tiny brightness sway over the steady field,
  // mirrors §02 so the two cinematics feel like one continuous breath.
  breathRate: number;     // rad/s
  breathAmp: number;      // 0..1
  seed: number;
}

const BUILD_CONFIG: BuildMeshConfig = {
  // Grid spacing. With cellSize 30 on a 400px canvas, placeCells() computes
  // floor((400 - 8) / 30) | 1 = floor(13.06) | 1 = 13 cols × 13 rows = 169
  // cells (the | 1 forces an odd count so one cell sits exactly at centre
  // for the Chebyshev wavefront). squareSize 4 keeps each filled square
  // crisp at this spacing.
  cellSize: 30,
  squareSize: 4,
  tipAlpha: 0.95,
  breathRate: 0.15,
  breathAmp: 0.10,
  seed: 0xc0a52e1,
};

// Per-cell intro timeline — kept short relative to BUILD_INTRO_DURATION so
// the wave-front reads as the dominant motion and individual cells don't
// linger in their copper phase.
const BUILD_INTRO_DURATION = 10.0;     // s — total intro window
const BUILD_CELL_BLOOM = 0.20;         // s — copper square fade-in
const BUILD_CELL_HOLD = 0.10;          // s — brief copper sustain
const BUILD_CELL_FLASH = 0.20;         // s — copper→white colour flash
const BUILD_CELL_TOTAL = BUILD_CELL_BLOOM + BUILD_CELL_HOLD + BUILD_CELL_FLASH;
const BUILD_RING_JITTER = 0.35;        // ±fraction of one ring-width per cell
const BUILD_CELL_GLOW_BLUR = 2.5;      // px — soft copper glow during bloom

// Sporadic copper re-ignitions after settle. Heart-beat cadence:
// random interval [MIN, MAX], single cell at a time. Per-pulse envelope
// is asymmetric — fast attack, brief hold, slow decay back to white.
const SPORADIC_PARALLEL = 6;           // N independent tracks running in parallel
const SPORADIC_INTERVAL_MIN = 1.0;     // s — minimum interval per track
const SPORADIC_INTERVAL_MAX = 3.0;     // s — maximum interval per track
const SPORADIC_BLOOM = 0.30;           // s — copper rise
const SPORADIC_HOLD = 1.20;            // s — copper sustain
const SPORADIC_FLASH = 0.50;           // s — copper→white return
const SPORADIC_TOTAL = SPORADIC_BLOOM + SPORADIC_HOLD + SPORADIC_FLASH;

interface Cell { x: number; y: number; wakeAt: number }
interface Pulse { cellIdx: number; startT: number }

class BuildMesh {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private cfg: BuildMeshConfig;
  private cssW = 0;
  private cssH = 0;
  private cells: Cell[] = [];
  private time = 0;
  private lastT = 0;
  private rafId = 0;
  private running = false;
  private inView = true;
  // Intro state — mirrors BurstField. introElapsed ticks only while inView
  // so an interrupted intro freezes rather than running invisibly.
  private introArmed = false;
  private introDone = false;
  private introElapsed = 0;
  // Sporadic post-settle copper. SPORADIC_PARALLEL independent tracks,
  // each holding its own next-fire time in this.time units (seconds
  // since start). `activePulses` is mutated each frame — expired pulses
  // removed, new ones spawned when any track's next-fire time elapses.
  private nextSporadicAt: number[] = [];
  private activePulses: Pulse[] = [];
  private rng: () => number;

  constructor(canvas: HTMLCanvasElement, cfg: BuildMeshConfig = BUILD_CONFIG) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
    this.cfg = cfg;
    this.rng = makeRng(cfg.seed);
    this.measure();
  }

  private measure(): void {
    const cssW = this.canvas.clientWidth;
    const cssH = this.canvas.clientHeight;
    if (cssW === 0 || cssH === 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(cssW * dpr);
    this.canvas.height = Math.round(cssH * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.cssW = cssW;
    this.cssH = cssH;
    this.placeCells();
  }

  private placeCells(): void {
    const c = this.cfg;
    // Odd cols/rows so the expansion has one cell exactly at centre.
    const cols = Math.max(3, Math.floor((this.cssW - 8) / c.cellSize) | 1);
    const rows = Math.max(3, Math.floor((this.cssH - 8) / c.cellSize) | 1);
    const halfC = (cols - 1) / 2;
    const halfR = (rows - 1) / 2;
    const totalW = (cols - 1) * c.cellSize;
    const totalH = (rows - 1) * c.cellSize;
    const offX = (this.cssW - totalW) / 2;
    const offY = (this.cssH - totalH) / 2;
    const maxD = Math.max(halfC, halfR);
    // Total wake spread across cells; last cell wakes at this much so it
    // still has room to complete its BUILD_CELL_TOTAL before t=DURATION.
    const wakeWindow = Math.max(0, BUILD_INTRO_DURATION - BUILD_CELL_TOTAL);
    const ringW = maxD > 0 ? wakeWindow / maxD : 0;
    const rng = makeRng(c.seed ^ 0xb1d51d);
    this.cells = [];
    for (let r = 0; r < rows; r++) {
      for (let col = 0; col < cols; col++) {
        const dCol = col - halfC;
        const dRow = r - halfR;
        // Chebyshev distance — wavefront is a square. Jitter within ±35%
        // of one ring width so the wavefront reads as organic, not a
        // perfectly-clocked geometric expansion.
        const cheby = Math.max(Math.abs(dCol), Math.abs(dRow));
        const baseWake = cheby * ringW;
        const jitter = (rng() - 0.5) * ringW * 2 * BUILD_RING_JITTER;
        const wakeAt = Math.max(0, Math.min(wakeWindow, baseWake + jitter));
        this.cells.push({
          x: offX + col * c.cellSize,
          y: offY + r * c.cellSize,
          wakeAt,
        });
      }
    }
  }

  setInView(v: boolean): void {
    if (this.inView === v) return;
    this.inView = v;
    // Suspend the rAF entirely when off-view instead of running it every
    // frame with render() skipped (stop() was previously dead code). time/
    // introElapsed only advance while in-view, and start() re-seeds lastT,
    // so the construction freezes and resumes from the same frame — visually
    // identical, just no wasted wakeups while scrolled away (N28).
    if (v) this.start();
    else this.stop();
  }

  resize(): void {
    this.measure();
    this.render();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastT = performance.now() / 1000;
    const tick = (now: number): void => {
      if (!this.running) return;
      const t = now / 1000;
      const dt = Math.min(0.05, t - this.lastT);
      this.lastT = t;
      if (this.inView && !document.hidden) {
        this.time += dt;
        if (this.introArmed && !this.introDone) this.introElapsed += dt;
        this.render();
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  /** Arm the one-shot §03 build. Called by IO when canvas first hits
   * ~40% viewport intersection. Idempotent. */
  triggerIntro(): void {
    if (this.introArmed || this.introDone) return;
    this.introArmed = true;
    this.introElapsed = 0;
  }

  /** Skip the intro entirely — used under prefers-reduced-motion so the
   * canvas paints the steady all-white grid immediately. */
  skipIntro(): void {
    this.introArmed = true;
    this.introDone = true;
  }

  render(): void {
    if (this.cssW === 0 || this.cssH === 0) return;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.cssW, this.cssH);

    // Pre-armed waiting state: canvas is empty. Visitor never sees this
    // for more than a beat — the IO fires at 40% viewport intersection.
    if (!this.introArmed && !this.introDone) return;

    if (this.introArmed && !this.introDone) {
      if (this.introElapsed < BUILD_INTRO_DURATION) {
        this.renderIntro(this.introElapsed);
        return;
      }
      this.introDone = true;
      // Schedule the first round of sporadic pulses across the N
      // tracks. Stagger initial fire times so tracks don't all fire at
      // once on settle — combine a fixed phase offset with a random
      // jitter so the cadence reads heart-beat, not metronome.
      const span = SPORADIC_INTERVAL_MAX - SPORADIC_INTERVAL_MIN;
      this.nextSporadicAt = [];
      for (let i = 0; i < SPORADIC_PARALLEL; i++) {
        const phase = (i / SPORADIC_PARALLEL) * SPORADIC_INTERVAL_MIN;
        this.nextSporadicAt[i] = this.time + phase + this.rng() * span;
      }
    }

    this.renderSettled();
  }

  // ── Intro render ─────────────────────────────────────────────────────
  // Per cell: copper bloom → copper hold → copper→white flash → settled.

  private renderIntro(localT: number): void {
    const c = this.cfg;
    const breath = 1 + c.breathAmp * Math.sin(this.time * c.breathRate);
    const targetA = clamp01(c.tipAlpha * breath);

    for (let i = 0; i < this.cells.length; i++) {
      const cell = this.cells[i];
      const s = localT - cell.wakeAt;
      if (s < 0) continue;

      if (s < BUILD_CELL_BLOOM) {
        // Copper bloom — gentle attack.
        const u = s / BUILD_CELL_BLOOM;
        this.drawCellBit(cell, 0, smoothstep01(u) * targetA);
      } else if (s < BUILD_CELL_BLOOM + BUILD_CELL_HOLD) {
        // Copper hold.
        this.drawCellBit(cell, 0, targetA);
      } else if (s < BUILD_CELL_TOTAL) {
        // Copper → white flash. rgb interpolates fast; alpha holds.
        const u = (s - BUILD_CELL_BLOOM - BUILD_CELL_HOLD) / BUILD_CELL_FLASH;
        this.drawCellBit(cell, easeOutCubic(u), targetA);
      } else {
        // Settled white square.
        this.drawCellBit(cell, 1, targetA);
      }
    }
  }

  // ── Settled render ───────────────────────────────────────────────────
  // Fully built mesh. All cells are white squares; on top, sporadic
  // copper pulses re-ignite one cell at a time on a heart-beat cadence.

  private renderSettled(): void {
    const c = this.cfg;
    const breath = 1 + c.breathAmp * Math.sin(this.time * c.breathRate);
    const targetA = clamp01(c.tipAlpha * breath);

    // Spawn the next sporadic pulse on any track whose nextSporadicAt
    // has elapsed. Each of the N tracks is independent — picks its own
    // random target cell and its own next-fire delay. Multiple tracks
    // can fire in the same frame; the active-pulses list grows and
    // shrinks naturally as pulses age out.
    const span = SPORADIC_INTERVAL_MAX - SPORADIC_INTERVAL_MIN;
    for (let trackIdx = 0; trackIdx < this.nextSporadicAt.length; trackIdx++) {
      if (this.time >= this.nextSporadicAt[trackIdx]) {
        const idx = Math.floor(this.rng() * this.cells.length);
        this.activePulses.push({ cellIdx: idx, startT: this.time });
        this.nextSporadicAt[trackIdx] = this.time + SPORADIC_INTERVAL_MIN + this.rng() * span;
      }
    }

    // Build a per-cell colour override map for cells currently pulsing.
    // Rare enough that a linear scan is fine — typically 0–1 pulses.
    const pulsing = new Map<number, number>(); // cellIdx → copperToWhiteT (0..1)
    for (let i = this.activePulses.length - 1; i >= 0; i--) {
      const p = this.activePulses[i];
      const s = this.time - p.startT;
      if (s >= SPORADIC_TOTAL) {
        this.activePulses.splice(i, 1);
        continue;
      }
      let t: number;
      if (s < SPORADIC_BLOOM) {
        // Fast attack — copper rising. Cell starts white, drifts to copper.
        t = 1 - smoothstep01(s / SPORADIC_BLOOM);
      } else if (s < SPORADIC_BLOOM + SPORADIC_HOLD) {
        t = 0; // copper hold
      } else {
        // Slow decay back to white.
        const u = (s - SPORADIC_BLOOM - SPORADIC_HOLD) / SPORADIC_FLASH;
        t = easeOutCubic(u);
      }
      pulsing.set(p.cellIdx, t);
    }

    for (let i = 0; i < this.cells.length; i++) {
      const cell = this.cells[i];
      const override = pulsing.get(i);
      this.drawCellBit(cell, override ?? 1, targetA);
    }
  }

  /** Draw a sharp small square at the cell whose rgb interpolates copper
   * (copperToWhiteT=0) → white (copperToWhiteT=1). A soft copper glow
   * fades out across the transition so the white settled state has no
   * halo, keeping the grid crisp. */
  private drawCellBit(cell: Cell, copperToWhiteT: number, alpha: number): void {
    const ctx = this.ctx;
    const c = this.cfg;
    const t = clamp01(copperToWhiteT);
    const g = Math.round(130 + (255 - 130) * t);
    const b = Math.round(76 + (255 - 76) * t);
    const glow = BUILD_CELL_GLOW_BLUR * (1 - t);
    const s = c.squareSize;
    ctx.save();
    if (glow > 0.1) {
      ctx.shadowColor = `rgba(${COPPER_RGB},${(alpha * 0.55).toFixed(3)})`;
      ctx.shadowBlur = glow;
    }
    ctx.fillStyle = `rgba(255,${g},${b},${alpha.toFixed(3)})`;
    ctx.fillRect(cell.x - s / 2, cell.y - s / 2, s, s);
    ctx.restore();
  }
}


// ── §04 cinematic — "the transfer mark" ────────────────────────────────────
//
// The §04 "How We Transfer" diagram is a single Bit (the .bp-transfer-mark
// span) breathing inside a frame. The breath is CSS-driven and never stops.
// On first viewport intersection, JS waits a beat so the visitor reads the
// copper bit clearly, then toggles a `.bp-transfer-mark--white` modifier
// which animates background-color and box-shadow over TRANSFER_FLASH seconds
// — the same copper→white grammar as §02 (per-tip handoff) and §03 (per-cell
// flash), one element at a time. Idempotent and one-shot.
const TRANSFER_COPPER_BEAT = 2.5;  // s — copper visible before transition starts
// CSS owns the colour-flash duration (1.5s); this comment exists so the
// link between the JS hold and the CSS transition is documented.

class TransferMark {
  private el: HTMLElement;
  private armed = false;
  private done = false;
  private timer = 0;

  constructor(el: HTMLElement) {
    this.el = el;
  }

  /** Arm the one-shot copper→white handoff. After TRANSFER_COPPER_BEAT
   * seconds (so the visitor reads the copper bit clearly), the CSS
   * `--handoff` modifier plays the energy→release animation. When it
   * finishes, swap to the static `--white` resting state and drop
   * `--handoff` so the white breath resumes. Idempotent. */
  triggerIntro(): void {
    if (this.armed || this.done) return;
    this.armed = true;
    this.timer = window.setTimeout(() => {
      this.el.addEventListener(
        'animationend',
        (e) => {
          if ((e as AnimationEvent).animationName !== 'bp-transfer-handoff') return;
          this.el.classList.add('bp-transfer-mark--white');
          this.el.classList.remove('bp-transfer-mark--handoff');
          this.done = true;
        },
        { once: true },
      );
      this.el.classList.add('bp-transfer-mark--handoff');
    }, TRANSFER_COPPER_BEAT * 1000);
  }

  /** Skip the intro entirely — applies the white state without the
   * copper hold or the colour transition. Used under reduced motion. */
  skipIntro(): void {
    if (this.done) return;
    if (this.timer) window.clearTimeout(this.timer);
    this.armed = true;
    this.done = true;
    // Suppress the CSS transition so the colour change is instantaneous.
    this.el.style.transition = 'none';
    this.el.classList.add('bp-transfer-mark--white');
  }
}

// ── §01 cinematic — "See. Build. Transfer." word-flash ─────────────────────
//
// The §01 headline is three words in a centred composition. Each word
// transitions gray → copper → white in sequence (~2s total) when the
// headline reaches viewport centre. CSS owns the per-word keyframe + the
// stagger via animation-delay; JS only adds the `.how-cue--armed`
// modifier when the trigger fires. One-shot.
//
// Trigger is velocity-aware:
// - Slow scroll: wait until the headline's centre is within a tight
//   band around the viewport centre (HOW_CUE_CENTRE_BAND px). The
//   visitor wants the cinematic to land when they're settled on it.
// - Fast scroll: trigger earlier (HOW_CUE_FAST_BAND px) so the
//   visitor doesn't blow past it before the animation can begin.
// - Velocity is a smoothed |dy/dt| in px/s, sampled on scroll.
const HOW_CUE_CENTRE_BAND = 80;     // px — slow-scroll trigger distance from viewport centre
const HOW_CUE_FAST_BAND = 280;      // px — fast-scroll trigger distance (looser)
const HOW_CUE_FAST_VELOCITY = 900;  // px/s — above this we treat scroll as fast
class HowCue {
  private el: HTMLElement;
  private armed = false;
  private rafScheduled = false;
  private lastScrollY = 0;
  private lastScrollT = 0;
  private velocity = 0;
  private onScroll = (): void => {
    const now = performance.now();
    const y = window.scrollY;
    const dt = now - this.lastScrollT;
    if (dt > 0) {
      const instant = Math.abs(y - this.lastScrollY) * 1000 / dt;
      // Exponential smoothing — keep ~150ms of history. Reacts to fast
      // flings without thrashing on tiny single-tick scrolls.
      this.velocity = this.velocity * 0.6 + instant * 0.4;
    }
    this.lastScrollY = y;
    this.lastScrollT = now;
    this.schedule();
  };

  constructor(el: HTMLElement) {
    this.el = el;
  }

  /** Begin watching scroll position. Called once at init. */
  attach(): void {
    if (this.armed) return;
    this.lastScrollY = window.scrollY;
    this.lastScrollT = performance.now();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // Schedule one check immediately so the cinematic fires on a fresh
    // page load that already has the headline near viewport centre
    // (e.g., refresh while scrolled).
    this.schedule();
  }

  private detach(): void {
    window.removeEventListener('scroll', this.onScroll);
  }

  private schedule(): void {
    if (this.rafScheduled || this.armed) return;
    this.rafScheduled = true;
    requestAnimationFrame(() => {
      this.rafScheduled = false;
      this.checkAndMaybeArm();
    });
  }

  private checkAndMaybeArm(): void {
    if (this.armed) return;
    const rect = this.el.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) return; // not in viewport at all
    const elCentre = rect.top + rect.height / 2;
    const viewportCentre = window.innerHeight / 2;
    const distFromCentre = Math.abs(elCentre - viewportCentre);
    const band = this.velocity > HOW_CUE_FAST_VELOCITY
      ? HOW_CUE_FAST_BAND
      : HOW_CUE_CENTRE_BAND;
    if (distFromCentre <= band) this.triggerIntro();
  }

  /** Arm the §01 word-flash. Adds the CSS modifier that kicks off the
   * keyframe animation on each .how-word child. Idempotent. Detaches
   * the scroll listener so we don't keep computing once armed. */
  triggerIntro(): void {
    if (this.armed) return;
    this.armed = true;
    this.el.classList.add('how-cue--armed');
    this.detach();
  }

  /** Reduced-motion path — settle the words to white immediately
   * without playing the animation. The CSS @media query already
   * disables the keyframe; calling this also adds the armed class so
   * the static state is unambiguous. */
  skipIntro(): void {
    this.armed = true;
    this.el.classList.add('how-cue--armed');
    this.detach();
  }
}

interface IntroTarget { triggerIntro(): void; skipIntro(): void }

interface Visual {
  render(): void;
  start(): void;
  setInView(v: boolean): void;
  resize(): void;
}

const visuals: Visual[] = [];


function initVisuals(): void {
  const burstCanvases = document.querySelectorAll<HTMLCanvasElement>('canvas[data-burst]');
  const meshCanvases = document.querySelectorAll<HTMLCanvasElement>('canvas[data-mesh]');
  const transferEls = document.querySelectorAll<HTMLElement>('[data-transfer-mark]');
  const howCueEls = document.querySelectorAll<HTMLElement>('[data-how-cue]');
  const all: HTMLCanvasElement[] = [];
  // §02 (BurstField), §03 (BuildMesh) and §04 (TransferMark) all share the
  // same intro contract — triggerIntro / skipIntro / one-shot IO. Track
  // them in a unified Element-keyed map so the intro observer can call
  // triggerIntro() blindly regardless of element type.
  const introTargets = new Map<Element, IntroTarget>();
  const burstFields: BurstField[] = [];
  const buildMeshes: BuildMesh[] = [];
  const transferMarks: TransferMark[] = [];
  const howCues: HowCue[] = [];

  for (const canvas of burstCanvases) {
    const v = new BurstField(canvas);
    visuals.push(v);
    all.push(canvas);
    introTargets.set(canvas, v);
    burstFields.push(v);
    // Under reduced motion, render the steady state immediately rather
    // than the pre-armed pupil-only state.
    if (REDUCED_MOTION) v.skipIntro();
    v.render();
    if (!REDUCED_MOTION) v.start();
  }
  for (const canvas of meshCanvases) {
    const v = new BuildMesh(canvas);
    visuals.push(v);
    all.push(canvas);
    introTargets.set(canvas, v);
    buildMeshes.push(v);
    if (REDUCED_MOTION) v.skipIntro();
    v.render();
    if (!REDUCED_MOTION) v.start();
  }
  for (const el of transferEls) {
    const tm = new TransferMark(el);
    transferMarks.push(tm);
    introTargets.set(el, tm);
    if (REDUCED_MOTION) tm.skipIntro();
  }
  for (const el of howCueEls) {
    const h = new HowCue(el);
    howCues.push(h);
    if (REDUCED_MOTION) h.skipIntro();
    else h.attach();
    // HowCue is self-watching: it tracks scroll velocity and fires when
    // the headline's centre reaches the viewport centre band (or earlier
    // when scrolling fast). Not added to introTargets — its trigger
    // is positional + velocity-aware, not a single IO threshold.
  }

  if (!REDUCED_MOTION && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = all.indexOf(entry.target as HTMLCanvasElement);
          if (idx >= 0 && visuals[idx]) visuals[idx].setInView(entry.isIntersecting);
        }
      },
      { rootMargin: '100px' },
    );
    for (const canvas of all) io.observe(canvas);

    // Mid-scroll / deep-link skip (N21): on a restored or deep-linked load
    // (e.g. arriving at #start, then scrolling up) any cinematic whose
    // section already sits above the viewport has effectively "already
    // played" — replaying construction when the visitor scrolls back up
    // reads as a glitch. Matching the hero's startsAtTop policy ("the
    // cinematic only reads at the top"), skip those intros up front and
    // don't observe them, so they render their settled state instead.
    // A section is "above the viewport" when its top edge has scrolled past
    // the viewport top (rect.top < 0). Targets still onscreen or below keep
    // their normal one-shot IO trigger.
    const sectionTopAboveViewport = (el: Element): boolean => {
      const section = el.closest('section') ?? el;
      return section.getBoundingClientRect().top < 0;
    };

    // One-shot cinematic trigger for §02 (BurstField), §03 (BuildMesh),
    // and §04 (TransferMark). Separate observer with no rootMargin and a
    // 40% threshold so each cinematic fires when its element is meaningfully
    // in view, not 100px before it enters the viewport.
    if (introTargets.size > 0) {
      const introIo = new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const v = introTargets.get(entry.target);
            if (v) {
              v.triggerIntro();
              observer.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.4 },
      );
      for (const [el, target] of introTargets) {
        if (sectionTopAboveViewport(el)) {
          // Already scrolled past at load — settle straight to the resting
          // state; never replay on a later scroll-up.
          target.skipIntro();
        } else {
          introIo.observe(el);
        }
      }
    }

  }

  // Dev-only handles for browser frame-by-frame verification of all three
  // cinematics. Stripped in production by Vite via import.meta.env.DEV.
  if (import.meta.env.DEV) {
    const w = window as unknown as {
      __burstFields?: BurstField[];
      __buildMeshes?: BuildMesh[];
      __transferMarks?: TransferMark[];
      __howCues?: HowCue[];
    };
    w.__burstFields = burstFields;
    w.__buildMeshes = buildMeshes;
    w.__transferMarks = transferMarks;
    w.__howCues = howCues;
  }
}

let resizeRaf = 0;
window.addEventListener('resize', () => {
  cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(() => {
    for (const v of visuals) v.resize();
  });
});

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  initVisuals();
} else {
  window.addEventListener('DOMContentLoaded', initVisuals);
}
